import { Lead, Message, ScheduledMessage, Note, InternalReminder } from '@/types';
import { 
  Send, Paperclip, Smile, Bot, User, Check, CheckCheck, Zap, Image, FileText, Mic, Play, Pause, Reply, X, Phone, Video as VideoIcon, 
  Search, Trash2, Forward, RotateCcw, Calendar, MessageSquare, Info, 
  UserRound, StickyNote, Bell, CalendarClock, Settings2, ExternalLink, CheckCircle2, PauseCircle, PlayCircle, Clock,
  ChevronLeft, ChevronRight, Plus, Upload, AlertCircle, RefreshCw, Download, Lock, CheckCircle, ShieldAlert, UserCheck, ShoppingBag,
  Wand2, Crop, RotateCw, Type, Pencil, ZoomIn, ZoomOut, Maximize2, Loader2
} from 'lucide-react';
import React, { useState, useRef, useEffect, ChangeEvent, useMemo, useCallback, useDeferredValue } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageListErrorBoundary } from './MessageListErrorBoundary';
import { cn, formatSafe, isTechnicalMessage, cleanMetaText, safeText, safeLower, safeTrim, normalizeMessageType, normalizeForDedupe, buildLeadUpdatePayload, getCleanedFriendlyPreview } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { api, apiCall } from '@/services/api';
import { queryClient } from '@/lib/query/queryClient';
import { supabase } from '@/lib/supabase';
import { useLeadsStore } from '@/store/leadsStore';
import { useConfigStore } from '@/store/configStore';
import { useConversationsStore, Conversation } from '@/store/conversationsStore';
import { useUsersStore } from '@/store/usersStore';
import { useLeadMessages } from '@/lib/query/useLeadMessages';
import { deduplicateVisualMessages } from '@/lib/deduplicateVisualMessages';
import { CannedResponsesAutocomplete } from './CannedResponsesAutocomplete';
import { useCannedResponsesStore } from '@/store/cannedResponsesStore';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';
import { useWhatsAppStore } from '@/store/whatsappStore';
import { useCatalogStore } from '@/store/catalogStore';
import { toast } from 'react-hot-toast';
import { LeadAvatar } from '../LeadAvatar';
import { LeadDrawer } from '@/components/LeadDrawer';
import { CatalogPicker } from './CatalogPicker';
import { EmojiPickerButton } from './EmojiPickerButton';
import { formatMessageText, resolveMentionsInText } from '@/utils/formatMessage';
import { ChatBubble } from '@/components/ui/whatsapp/chat-bubble';
import { ChatHeader } from '@/components/ui/whatsapp/chat-header';
import { DateSeparator } from '@/components/ui/whatsapp/date-separator';
import { SystemMessageBubble } from '@/components/ui/whatsapp/system-message-bubble';
import { VoiceMessageBubble } from '@/components/ui/whatsapp/voice-message-bubble';
import { ImageBubble } from '@/components/ui/whatsapp/image-bubble';
import { VideoBubble } from '@/components/ui/whatsapp/video-bubble';
import { FileAttachmentBubble } from '@/components/ui/whatsapp/file-attachment-bubble';
import { UnsupportedMessageBubble } from '@/components/ui/whatsapp/unsupported-message-bubble';
import { ReplyPreview } from '@/components/ui/whatsapp/reply-preview';
import { TypingIndicator } from '@/components/ui/whatsapp/typing-indicator';

const MESSAGES_TABLE = import.meta.env.VITE_SUPABASE_MESSAGES_TABLE || 'crm_messages';

// ============================================================
// 🎯 CARD DE CONTEXTO DE PRODUTO — Estilo Meta Ads (M15)
// Visual inspirado nos cards de anúncio do Facebook/Instagram
// ============================================================
const productContextCache = new Map<string, any>();

const ProductContextCard = ({ produtoId }: { produtoId: string }) => {
  const [produto, setProduto] = useState<any>(null);
  const [loading, setLoading] = useState(!productContextCache.has(produtoId));
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (productContextCache.has(produtoId)) {
      setProduto(productContextCache.get(produtoId));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCall(`/api/catalog/items/${produtoId}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) {
            productContextCache.set(produtoId, data);
            setProduto(data);
          }
        }
      } catch (e) {
        console.error('[ProductContextCard] Erro ao buscar produto:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [produtoId]);

  // Skeleton loader estilo Meta
  if (loading) {
    return (
      <div className="mb-2 max-w-[340px] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1A1D21] shadow-sm">
        <div className="m-3 p-3 rounded-lg bg-gray-50 dark:bg-[#111B21] flex gap-3">
          <div className="w-20 h-20 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse shrink-0" />
          <div className="flex-1 space-y-2 py-1">
            <div className="h-3.5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-3 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-2.5 w-2/3 bg-gray-100 dark:bg-gray-800 rounded animate-pulse mt-2" />
          </div>
        </div>
      </div>
    );
  }

  if (!produto) return null;

  const imageUrl = !imgError && Array.isArray(produto.imagens) && produto.imagens.length > 0 ? produto.imagens[0] : null;
  const preco = produto.preco ? Number(produto.preco).toLocaleString('pt-BR', { style: 'currency', currency: produto.moeda || 'BRL' }) : null;
  const link = produto.link_produto || null;

  return (
    <div className="mb-2 max-w-[340px] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1A1D21] shadow-sm hover:shadow-md transition-shadow">
      {/* Bloco interno estilo Meta (fundo cinza claro) */}
      <div className="m-3 p-3 rounded-lg bg-gray-50 dark:bg-[#111B21] flex gap-3">
        {/* Imagem 80x80 */}
        {imageUrl ? (
          <div className="relative w-20 h-20 shrink-0">
            <img
              src={imageUrl}
              alt={produto.nome}
              className="w-20 h-20 rounded-lg object-cover bg-gray-200 dark:bg-gray-700"
              onError={() => setImgError(true)}
            />
            {/* Ícone sutil do Facebook no canto da imagem, como no print */}
            <div className="absolute bottom-1 right-1 bg-white rounded-full p-0.5 shadow-sm">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </div>
          </div>
        ) : (
          <div className="w-20 h-20 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
            <ShoppingBag size={28} className="text-gray-400" />
          </div>
        )}

        {/* Info do produto */}
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
          <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight line-clamp-2">
            {produto.nome}
          </p>
          {preco && (
            <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mt-0.5">
              {preco}
            </p>
          )}
          {produto.tamanho && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Tam: {produto.tamanho}
            </p>
          )}
          {produto.cores && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Cor: {produto.cores}
            </p>
          )}
        </div>
      </div>

      {/* Footer com selo de anúncio */}
      <div className="px-3 pb-2.5 -mt-1 flex items-center gap-1.5">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-blue-600 dark:text-blue-400">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
        </svg>
        <span className="text-[11px] text-blue-600 dark:text-blue-400 font-medium">
          Veio de anúncio do Meta
        </span>
      </div>
    </div>
  );
};
// ============================================================
// FIM ProductContextCard
// ============================================================

const WhatsAppAudioPlayer = ({ 
  url, 
  messageId,
  initialTranscription,
  onPlaybackChange,
  onTranscriptionSuccess,
  variant = 'incoming',
  timestamp,
  status,
  showTail = false
}: { 
  url: string; 
  messageId?: string;
  initialTranscription?: string;
  onPlaybackChange?: (isPlaying: boolean) => void;
  onTranscriptionSuccess?: (text: string) => void;
  variant?: 'incoming' | 'outgoing';
  timestamp?: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'imported';
  showTail?: boolean;
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasBeenPlayed, setHasBeenPlayed] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [transcription, setTranscription] = useState<string | null>(initialTranscription || null);
  const [isTranscribing, setIsTranscribing] = useState(false);

  useEffect(() => {
    if (initialTranscription) {
      setTranscription(initialTranscription);
    }
  }, [initialTranscription]);

  // Generate a predictable, neat set of waveform heights based on the URL (so each audio looks unique but consistent)
  const waveBars = useMemo(() => {
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      hash = url.charCodeAt(i) + ((hash << 5) - hash);
    }
    const bars: number[] = [];
    for (let i = 0; i < 28; i++) {
      const pseudoRandom = Math.abs(Math.sin(hash + i)) * 100;
      // Let's keep height between 20% and 90%
      const height = Math.max(20, Math.min(90, Math.floor(pseudoRandom)));
      bars.push(height);
    }
    return bars;
  }, [url]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => {
      setIsPlaying(true);
      setHasBeenPlayed(true);
      audio.playbackRate = playbackSpeed;
      onPlaybackChange?.(true);
    };
    const handlePause = () => {
      setIsPlaying(false);
      onPlaybackChange?.(false);
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.currentTime > 0) {
        setHasBeenPlayed(true);
      }
    };
    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleDurationChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onPlaybackChange?.(false);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);

    // Initial check in case it's loaded
    if (audio.duration && isFinite(audio.duration)) {
      setDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      // FIX-E: se o componente desmontar enquanto tocava (ex: usuário trocou de lead),
      // garante que o id sai do set de "tocando" — evita travar o agrupamento pra sempre.
      onPlaybackChange?.(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, playbackSpeed]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().then(() => {
        audio.playbackRate = playbackSpeed;
      }).catch(err => console.error("Audio playback error:", err));
    }
  };

  const handleBarClick = (index: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const targetPercentage = index / waveBars.length;
    audio.currentTime = targetPercentage * duration;
    setHasBeenPlayed(true);
  };

  const togglePlaybackSpeed = () => {
    const nextSpeed = playbackSpeed === 1 ? 1.5 : playbackSpeed === 1.5 ? 2 : 1;
    setPlaybackSpeed(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  const handleTranscribe = async () => {
    if (!messageId || isTranscribing) return;
    setIsTranscribing(true);
    try {
      const res = await apiCall('/api/ai/transcribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message_id: messageId })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Erro ao transcrever áudio');
      }
      if (data.transcription) {
        setTranscription(data.transcription);
        onTranscriptionSuccess?.(data.transcription);
        toast.success('Áudio transcrito com sucesso!');
      }
    } catch (err: any) {
      console.error('[TRANSCRIBE_ERR]', err);
      toast.error(err.message || 'Erro ao transcrever áudio');
    } finally {
      setIsTranscribing(false);
    }
  };

  const formatAudioTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const progressPercentage = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex flex-col w-full">
      <audio ref={audioRef} src={url} preload="metadata" className="hidden" />
      <VoiceMessageBubble
        variant={variant}
        duration={isPlaying ? formatAudioTime(currentTime) : formatAudioTime(duration || 0)}
        timestamp={timestamp}
        status={status}
        showTail={showTail}
        isPlaying={isPlaying}
        progress={progressPercentage}
        onPlayPause={togglePlay}
        footer={
          <div className="flex flex-col gap-1 w-full pt-1">
            <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-[#8696a0] font-normal">
              <div className="flex items-center gap-1.5">
                {messageId && !transcription && (
                  <button
                    type="button"
                    disabled={isTranscribing}
                    onClick={handleTranscribe}
                    className="text-[10.5px] font-semibold flex items-center gap-1 bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-md transition-all cursor-pointer disabled:opacity-50 select-none active:scale-95"
                    title="Transcrever áudio com IA"
                  >
                    {isTranscribing ? (
                      <>
                        <Loader2 size={11} className="animate-spin text-emerald-500" />
                        <span>Transcrevendo...</span>
                      </>
                    ) : (
                      <>
                        <FileText size={11} />
                        <span>Transcrever</span>
                      </>
                    )}
                  </button>
                )}

                <button
                  type="button"
                  onClick={togglePlaybackSpeed}
                  className="text-[10px] font-extrabold bg-black/5 hover:bg-black/10 dark:bg-white/5 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-md select-none transition-all cursor-pointer active:scale-95"
                  title="Alterar Velocidade"
                >
                  {playbackSpeed}x
                </button>
              </div>
            </div>

            {/* U4: Texto da Transcrição do Áudio */}
            {transcription && (
              <div className="mt-1 p-2 rounded-lg bg-black/[0.03] dark:bg-black/25 border border-black/5 dark:border-white/5 text-[12px] leading-relaxed animate-in fade-in duration-200">
                <div className="flex items-center gap-1 font-semibold text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  <FileText size={11} className="text-emerald-500" />
                  <span>Transcrição do áudio</span>
                </div>
                <p className="italic text-gray-700 dark:text-gray-300 select-text">
                  "{transcription}"
                </p>
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

const RecordingWaveform = ({ stream, isRecording }: { stream: MediaStream | null; isRecording: boolean }) => {
  const [bars, setBars] = useState<number[]>(new Array(25).fill(15));
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isRecording || !stream) {
      // Just some static low values
      setBars(new Array(25).fill(15));
      return;
    }

    let audioCtx: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let isCancelled = false;

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        audioCtx = new AudioContextClass();
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 64; // Small fftSize is enough for 25-30 bars
        source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateWave = () => {
          if (isCancelled) return;
          if (analyser) {
            analyser.getByteFrequencyData(dataArray);
            const newBars = Array.from({ length: 25 }, (_, i) => {
              const dataIdx = Math.floor((i / 25) * bufferLength);
              const val = dataArray[dataIdx] || 0;
              return Math.max(12, Math.min(90, Math.floor((val / 255) * 80) + 12));
            });
            setBars(newBars);
          }
          animationRef.current = requestAnimationFrame(updateWave);
        };

        updateWave();
      }
    } catch (err) {
      console.warn("Failed to initialize Web Audio Analyser (expected in some sandbox iframes):", err);
      const interval = setInterval(() => {
        if (isCancelled) return;
        const newBars = Array.from({ length: 25 }, () => {
          return Math.max(15, Math.min(85, Math.floor(Math.random() * 70) + 15));
        });
        setBars(newBars);
      }, 100);

      return () => {
        isCancelled = true;
        clearInterval(interval);
      };
    }

    return () => {
      isCancelled = true;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      try {
        if (source) source.disconnect();
        if (audioCtx && audioCtx.state !== 'closed') {
          audioCtx.close();
        }
      } catch (e) {
        // ignore
      }
    };
  }, [stream, isRecording]);

  return (
    <div className="flex items-end gap-[3px] h-8 px-4 py-1 select-none w-full justify-center">
      {bars.map((barHeight, idx) => (
        <div 
          key={idx}
          style={{ height: `${barHeight}%` }}
          className="w-[3px] bg-red-500 dark:bg-red-400 rounded-full transition-all duration-75"
        />
      ))}
    </div>
  );
};

// ============================================================
// 🎯 MEDIA DEMAND — Carregamento Dinâmico com Retry e Transcrição (U4 & U5)
// ============================================================
const MediaDemand = ({ 
  type, 
  url: initialUrl, 
  messageId,
  downloadName, 
  onImageClick, 
  onVideoClick,
  onPlaybackChange,
  onMediaResolved,
  initialTranscription,
  onTranscriptionSuccess,
  variant = 'incoming',
  timestamp,
  status,
  caption,
  showTail = false,
  onSameUrlReload,
  onImageError,
  onRecover,
  contextKey,
  reloadKey = 0
}: { 
  type: 'audio' | 'video' | 'image' | 'document'; 
  url: string; 
  messageId?: string;
  downloadName?: string; 
  onImageClick?: (url: string) => void; 
  onVideoClick?: (url: string) => void;
  onPlaybackChange?: (isPlaying: boolean) => void;
  onMediaResolved?: (newUrl: string) => void;
  initialTranscription?: string;
  onTranscriptionSuccess?: (text: string) => void;
  variant?: 'incoming' | 'outgoing';
  timestamp?: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'imported';
  caption?: string;
  showTail?: boolean;
  onSameUrlReload?: (msgId: string) => void;
  onImageError?: (messageId: string, url: string) => void;
  onRecover?: (messageId: string, url: string) => Promise<boolean>;
  contextKey?: string;
  reloadKey?: number;
}) => {
  const [currentUrl, setCurrentUrl] = useState(initialUrl);
  const [retryCount, setRetryCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const resolutionEpochRef = useRef<object>({});
  const pollingRef = useRef<object | null>(null);

  React.useLayoutEffect(() => {
    resolutionEpochRef.current = {};
    pollingRef.current = null;
    setCurrentUrl(initialUrl);
    setMediaError(false);
    setRetryCount(0);
    setIsChecking(false);
    setLoaded(Boolean(initialUrl && !initialUrl.includes('.enc') && initialUrl.trim()));
    return () => { resolutionEpochRef.current = {}; pollingRef.current = null; };
  }, [initialUrl, messageId, contextKey]);

  const isEncrypted = Boolean(currentUrl && currentUrl.includes('.enc'));
  const isEmpty = !currentUrl || currentUrl.trim() === '';
  const shouldAutoLoad = Boolean(currentUrl && !isEncrypted && !isEmpty);
  const [loaded, setLoaded] = useState(shouldAutoLoad);

  // V5: resetar `loaded` quando URL mudar (ex: .enc → URL Supabase via Realtime UPDATE)
  useEffect(() => {
    if (currentUrl && !currentUrl.includes('.enc') && currentUrl.trim() !== '') {
      setLoaded(true);
    }
  }, [currentUrl]);

  // U5: Polling curto para verificar se webhook concluiu upload no storage (a cada 4s, até 3 vezes)
  const checkMediaResolution = useCallback(async () => {
    if (!messageId || pollingRef.current) return false;
    const flight = {};
    const epoch = resolutionEpochRef.current;
    pollingRef.current = flight;
    setIsChecking(true);
    try {
      const { data } = await supabase
        .from('crm_messages')
        .select('media_url')
        .eq('id', messageId)
        .maybeSingle();

      if (resolutionEpochRef.current !== epoch) return false;
      if (data?.media_url && !data.media_url.includes('.enc') && data.media_url.trim() !== '') {
        setCurrentUrl(data.media_url);
        setLoaded(true);
        onMediaResolved?.(data.media_url);
        return true;
      }
    } catch (e) {
      // Falhas de consulta continuam retentáveis, sem expor detalhes do transporte.
    } finally {
      if (pollingRef.current === flight) {
        pollingRef.current = null;
        if (resolutionEpochRef.current === epoch) setIsChecking(false);
      }
    }
    return false;
  }, [messageId, isChecking, onMediaResolved]);

  useEffect(() => {
    if (isEmpty && messageId && retryCount < 3) {
      const epoch = resolutionEpochRef.current;
      const timer = setTimeout(async () => {
        const resolved = await checkMediaResolution();
        if (!resolved && resolutionEpochRef.current === epoch) {
          setRetryCount(prev => prev + 1);
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [isEmpty, messageId, retryCount, checkMediaResolution]);

  // AA5: Se retryCount >= 3 tentativas sem sucesso, exibir fallback com link direto
  if (retryCount >= 3 && type !== 'image') {
    if (currentUrl && currentUrl.trim() !== '') {
      return (
        <a
          href={currentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block no-underline cursor-pointer"
        >
          <UnsupportedMessageBubble
            variant={variant}
            title="Mídia indisponível no servidor"
            description="Mídia indisponível no servidor — abrir original"
            timestamp={timestamp}
            status={status}
            showTail={showTail}
          />
        </a>
      );
    }
    return (
      <div
        onClick={() => {
          setRetryCount(0);
          checkMediaResolution();
        }}
        className="cursor-pointer"
      >
        <UnsupportedMessageBubble
          variant={variant}
          title="Mídia indisponível no servidor"
          description="Mídia indisponível no servidor — clique para tentar novamente"
          timestamp={timestamp}
          status={status}
          showTail={showTail}
        />
      </div>
    );
  }

  // U5: Se URL nula ou vazia (em processamento pelo servidor)
  if (isEmpty && type !== 'image') {
    return (
      <div onClick={() => checkMediaResolution()} className="cursor-pointer">
        <UnsupportedMessageBubble 
          variant={variant}
          title="Processando mídia..."
          description={isChecking ? "Verificando se o upload concluiu..." : "Mídia em processamento... clique para verificar"}
          timestamp={timestamp}
          status={status}
          showTail={showTail}
        />
      </div>
    );
  }

  // Se URL terminar em .enc (criptografada), exibe o aviso "Arquivo criptografado"
  if (isEncrypted && !loaded && type !== 'image') {
    return (
      <div onClick={() => setLoaded(true)} className="cursor-pointer">
        <UnsupportedMessageBubble 
          variant={variant}
          title="Arquivo criptografado"
          description="Clique para abrir ou baixar o arquivo"
          timestamp={timestamp}
          status={status}
          showTail={showTail}
        />
      </div>
    );
  }

  if (type === 'audio') {
    return (
      <WhatsAppAudioPlayer 
        url={currentUrl} 
        messageId={messageId}
        initialTranscription={initialTranscription}
        onPlaybackChange={onPlaybackChange} 
        onTranscriptionSuccess={onTranscriptionSuccess}
        variant={variant}
        timestamp={timestamp}
        status={status}
        showTail={showTail}
      />
    );
  }
  if (type === 'video') {
    return (
      <VideoBubble
        variant={variant}
        src={currentUrl}
        caption={caption}
        timestamp={timestamp}
        status={status}
        showTail={showTail}
        onExpand={() => onVideoClick?.(currentUrl)}
      />
    );
  }
  if (type === 'image') {
    return (
      <ImageBubble
        variant={variant}
        src={currentUrl}
        caption={caption}
        timestamp={timestamp}
        status={status}
        showTail={showTail}
        messageId={messageId}
        reloadKeys={messageId ? { [messageId]: reloadKey } : undefined}
        onImageError={onImageError}
        onRecover={onRecover}
        onClick={() => {
          if (onImageClick) {
            onImageClick(currentUrl);
          }
        }}
        className="cursor-pointer"
      />
    );
  }
  if (type === 'document') {
    const ext = currentUrl.split('.').pop()?.split('?')[0] || 'pdf';
    return (
      <FileAttachmentBubble
        variant={variant}
        fileName={downloadName || 'Documento'}
        fileType={ext}
        downloadUrl={currentUrl}
        timestamp={timestamp}
        status={status}
        showTail={showTail}
      />
    );
  }
  return null;
};

// ============================================================
// 🎯 U2 / AA2: SUBCOMPONENTE DE TEXTO EXPANSÍVEL ("LER MAIS" / "MOSTRAR MENOS")
// ============================================================
interface ExpandableMessageTextProps {
  content: string;
  renderText: (text: string) => React.ReactNode;
}

const ExpandableMessageText: React.FC<ExpandableMessageTextProps> = ({ content, renderText }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClampable, setIsClampable] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isExpanded && textRef.current) {
      const el = textRef.current;
      setIsClampable(el.scrollHeight > el.clientHeight + 8);
    }
  }, [content, isExpanded]);

  return (
    <div className="flex flex-col min-w-0">
      <div
        ref={textRef}
        className={cn(
          "whitespace-pre-wrap break-words min-w-0",
          !isExpanded && "line-clamp-6 overflow-hidden"
        )}
      >
        {renderText(content)}
      </div>
      {isClampable && (
        <button
          type="button"
          onClick={() => setIsExpanded(prev => !prev)}
          className="mt-1 self-start text-[12px] text-blue-500 hover:text-blue-600 dark:text-blue-400 font-semibold cursor-pointer select-none transition-colors"
        >
          {isExpanded ? 'Mostrar menos' : 'Ler mais'}
        </button>
      )}
    </div>
  );
};

const getParticipantColor = (name: string) => {
  const colors = [
    'text-pink-600 dark:text-pink-400 bg-pink-100 dark:bg-pink-950/40',
    'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-950/40',
    'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/40',
    'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-950/40',
    'text-violet-600 dark:text-violet-400 bg-violet-100 dark:bg-violet-950/40',
    'text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-950/40',
    'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-950/40',
    'text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-950/40'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const getParticipantInitials = (name: string) => {
  if (!name) return 'U';
  const clean = name.replace(/^~/, '').trim();
  const parts = clean.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return clean.substring(0, 2).toUpperCase();
};

const getParticipantTextColor = (name: string) => {
  const colors = [
    'text-pink-500 dark:text-pink-400',
    'text-blue-500 dark:text-sky-400',
    'text-emerald-600 dark:text-emerald-400',
    'text-amber-600 dark:text-amber-400',
    'text-purple-600 dark:text-purple-400',
    'text-rose-500 dark:text-rose-400',
    'text-indigo-600 dark:text-indigo-400',
    'text-cyan-600 dark:text-cyan-400',
    'text-teal-600 dark:text-teal-400',
    'text-orange-500 dark:text-orange-400'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

// V4: Agrupar mensagens de mídia consecutivas (igual WhatsApp)
const groupConsecutiveMedia = (msgs: Message[]): Array<Message & { is_media_group?: boolean; messages?: Message[] }> => {
  const result: Array<Message & { is_media_group?: boolean; messages?: Message[] }> = [];
  let i = 0;
  while (i < msgs.length) {
    const msg = msgs[i];
    // V6: agrupar por message_type (image|video) — NÃO exige media_url.
    // A grade 2x2 aparece INSTANTANEAMENTE quando as mensagens chegam via realtime,
    // com spinners nas posições ainda sem URL. Assim que o backend popula media_url
    // (fila de ~500ms/mídia), o spinner vira imagem sem re-ordenar nada.
    if (msg.message_type === 'image' || msg.message_type === 'video') {
      const group: Message[] = [msg];
      let j = i + 1;
      while (j < msgs.length) {
        const next = msgs[j];
        const sameDirection = next.message_direction === msg.message_direction;
        const isMedia = next.message_type === 'image' || next.message_type === 'video';
        const timeDiff = Math.abs(new Date(next.created_at).getTime() - new Date(msg.created_at).getTime());
        const samePrivate = next.is_private === msg.is_private;
        // Use the same browser-local calendar as separators and message times.
        // Even a 30s album must not swallow the next day's boundary.
        const sameDay = new Date(next.created_at).toDateString() === new Date(msg.created_at).toDateString();
        // V5: janela ampliada para 30s (era 5s) — acomoda latência do webhook + fila de 500ms/mídia no backend
        // para que álbuns do WhatsApp (até ~10 imagens) agrupem mesmo com dedupe/queue spread.
        if (sameDirection && isMedia && timeDiff < 30000 && samePrivate && sameDay) {
          group.push(next);
          j++;
        } else {
          break;
        }
      }
      if (group.length > 1) {
        result.push({
          ...msg,
          is_media_group: true,
          messages: group,
          // FIX-A: NÃO mudar o id ao agrupar — manter o id original da primeira mensagem
          // pra que a key do React continue estável entre renders quando o agrupamento muda
          // (ex: nova mídia chega via realtime e entra no grupo existente).
          // Antes era "group-${msg.id}" que mudava a key e causava removeChild crash.
          id: msg.id
        });
        i = j;
      } else {
        result.push(msg);
        i++;
      }
    } else {
      result.push(msg);
      i++;
    }
  }
  return result;
};

interface Props {
  lead: Lead;
  conversation?: Conversation | null;
  onBack?: () => void;
}

export function ChatArea({ lead, conversation, onBack }: Props) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [lightboxVideo, setLightboxVideo] = useState<string | null>(null);
  const updateLeadLocally = useLeadsStore(state => state.updateLeadLocally);
  const { updateStatus, updatePriority, resolveConversation, updateLocally: updateConversationLocally } = useConversationsStore();
  
  const connections = useWhatsAppStore(state => state.connections);
  const fetchConnections = useWhatsAppStore(state => state.fetchConnections);

  const user = useAuthStore(state => state.user);
  const profile = useAuthStore(state => state.profile);

  const users = useUsersStore(state => state.users);
  const fetchUsers = useUsersStore(state => state.fetchUsers);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const assignedUserName = useMemo(() => {
    if (!conversation?.assigned_user_id) return null;
    const targetId = conversation.assigned_user_id;
    const u = users.find(x => x.id === targetId || (x.auth_user_id && x.auth_user_id === targetId));
    return u?.nome || u?.email?.split('@')[0] || conversation.assigned_user_name || null;
  }, [conversation?.assigned_user_id, conversation?.assigned_user_name, users]);

  // P-ASSIGN-ID: Mapeamento de todos os identificadores conhecidos do usuário autenticado
  const myUserIds = useMemo(() => {
    const ids = new Set<string>();
    if (profile?.id) ids.add(profile.id);
    if (profile?.auth_user_id) ids.add(profile.auth_user_id);
    if ((profile as any)?.crm_user_id) ids.add((profile as any).crm_user_id);
    if (user?.id) ids.add(user.id);

    const matchedUser = users.find(u =>
      (profile?.id && (u.id === profile.id || u.auth_user_id === profile.id)) ||
      (profile?.auth_user_id && (u.id === profile.auth_user_id || u.auth_user_id === profile.auth_user_id)) ||
      (user?.id && (u.id === user.id || u.auth_user_id === user.id)) ||
      (profile?.email && u.email?.toLowerCase() === profile.email.toLowerCase())
    );
    if (matchedUser) {
      if (matchedUser.id) ids.add(matchedUser.id);
      if (matchedUser.auth_user_id) ids.add(matchedUser.auth_user_id);
    }
    return ids;
  }, [profile, user, users]);

  const isMyConversation = useMemo(() => {
    if (!conversation) return false;
    return Boolean(
      (conversation.assigned_user_id && myUserIds.has(conversation.assigned_user_id)) ||
      (conversation.assigned_auth_user_id && myUserIds.has(conversation.assigned_auth_user_id)) ||
      (conversation.assigned_crm_user_id && myUserIds.has(conversation.assigned_crm_user_id))
    );
  }, [conversation, myUserIds]);

  const handleAssign = async () => {
    if (!conversation) return;
    try {
      const res = await apiCall(`/api/conversations/${conversation.id}/assign`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || 'Não foi possível assumir.');
        return;
      }
      toast.success('Você assumiu esta conversa.');
      const currentUId = profile?.id || user?.id;
      updateConversationLocally(conversation.id, {
        assigned_user_id: currentUId,
        assigned_crm_user_id: profile?.id || (profile as any)?.crm_user_id || currentUId,
        assigned_auth_user_id: profile?.auth_user_id || user?.id || null,
        assigned_at: new Date().toISOString()
      });
      if (conversation.lead_id) {
        updateLeadLocally(conversation.lead_id, {
          responsavel_user_id: currentUId
        });
      }
      // "Minhas" is backed by the conversation query, not only optimistic stores.
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } catch (err) {
      toast.error('Erro ao assumir conversa.');
    }
  };

  const handleRelease = async () => {
    if (!conversation) return;
    try {
      const res = await apiCall(`/api/conversations/${conversation.id}/release`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || 'Não foi possível liberar.');
        return;
      }
      toast.success('Conversa liberada.');
      updateConversationLocally(conversation.id, {
        assigned_user_id: null,
        assigned_at: null
      });
      if (conversation.lead_id) {
        updateLeadLocally(conversation.lead_id, {
          responsavel_user_id: null
        });
      }
    } catch (err) {
      toast.error('Erro ao liberar conversa.');
    }
  };

  // [CORRECAO-05] Retomada da IA sem confirm() nativo (suspeito de travar em webview):
  // 1º clique arma por 6s, 2º clique confirma. Nenhum envio de mensagem aqui —
  // a IA responde na próxima mensagem do lead.
  const reactivateArmRef = useRef(0);

  const handleReactivateAI = async () => {
    if (!conversation) return;
    const now = Date.now();
    if (now - reactivateArmRef.current > 6000) {
      reactivateArmRef.current = now;
      toast('Clique novamente para confirmar: a IA vai voltar a responder esta conversa.');
      return;
    }
    reactivateArmRef.current = 0;
    try {
      const res = await apiCall(`/api/ai/conversations/${conversation.id}/reactivate`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || 'Não foi possível reativar a IA.');
        return;
      }
      toast.success('IA reativada! Ela responde na próxima mensagem do lead.');
      updateConversationLocally(conversation.id, {
        assigned_user_id: null,
        assigned_at: null
      });
      if (conversation.lead_id) {
        updateLeadLocally(conversation.lead_id, {
          responsavel_user_id: null
        });
      }
    } catch (err) {
      toast.error('Erro ao reativar IA. Tente novamente.');
    }
  };

  const handleAssume = async () => {
    if (!conversation) return;
    try {
      const res = await apiCall(`/api/conversations/${conversation.id}/assume`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || 'Não foi possível assumir.');
        return;
      }
      toast.success('Você assumiu esta conversa de transbordo!');
      const updatedData = await res.json().catch(() => ({}));
      const currentUId = profile?.id || user?.id;
      updateConversationLocally(conversation.id, {
        assigned_user_id: currentUId,
        assigned_crm_user_id: profile?.id || (profile as any)?.crm_user_id || currentUId,
        assigned_auth_user_id: profile?.auth_user_id || user?.id || null,
        assigned_at: new Date().toISOString(),
        sla_exceeded: false,
        sla_exceeded_at: null,
        ...updatedData
      });
      if (conversation.lead_id) {
        updateLeadLocally(conversation.lead_id, {
          responsavel_user_id: currentUId
        });
      }
    } catch (err) {
      toast.error('Erro ao assumir conversa de transbordo.');
    }
  };

  const connectionsLoading = useWhatsAppStore(state => state.loading);
  const hasConnectedInstance = useMemo(() => {
    return connections.some(c => c.status === 'connected');
  }, [connections]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const catalogEnabled = useCatalogStore(state => state.catalogEnabled);
  const fetchCatalogConfig = useCatalogStore(state => state.fetchConfig);
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);

  useEffect(() => {
    fetchCatalogConfig();
  }, [fetchCatalogConfig]);

  const [messageListResetKey, setMessageListResetKey] = useState(0);
  const {
    messages,
    isLoading: messagesLoading,
    isFetching: messagesFetching,
    isFetchingNextPage: loadingMore,
    hasMore,
    isError: messagesError,
    loadMore: fetchMoreMessages,
    refetch: refetchMessages,
    addRealtimeMessage,
    updateMessageInCache,
    removeMessageFromCache,
    rollbackDeletedMessage,
    addOptimisticMessage,
    replaceOptimisticWithReal,
    markOptimisticFailed
  } = useLeadMessages(lead.id, {
    userId: profile?.auth_user_id || profile?.id || user?.id,
    lojaId: lead.loja_id,
    tenantId: lead.tenant_id
  });
  // Fresh cache is immediately useful, but contains no events received while
  // this chat was unmounted. Reconcile once AFTER subscribing, in background,
  // using the existing authoritative recovery (including edits/deletions).
  const messageScopeKey = JSON.stringify([lead.id, profile?.auth_user_id || profile?.id || user?.id, lead.tenant_id, lead.loja_id]);
  const openingHistoryRef = useRef({ key: messageScopeKey, needsSync: !messagesLoading });
  if (openingHistoryRef.current.key !== messageScopeKey) {
    openingHistoryRef.current = { key: messageScopeKey, needsSync: !messagesLoading };
  }
  const messagesFetchingRef = useRef(messagesFetching);
  messagesFetchingRef.current = messagesFetching;
  const recoveryAttemptsRef = useRef<Set<string>>(new Set());
  const mediaContextKey = JSON.stringify([lead.id, lead.tenant_id, lead.loja_id]);
  const recoveryFlightsRef = useRef(new Map<string, { promise: Promise<boolean> }>());
  const recoveryEpochRef = useRef<object>({});
  const recoveryMountedRef = useRef(false);
  const [reloadKeys, setReloadKeys] = useState<Record<string, number>>({});

  React.useLayoutEffect(() => {
    const epoch = {};
    recoveryEpochRef.current = epoch;
    recoveryMountedRef.current = true;
    recoveryAttemptsRef.current.clear();
    recoveryFlightsRef.current.clear();
    setReloadKeys({});
    return () => {
      recoveryMountedRef.current = false;
      recoveryEpochRef.current = {};
      recoveryFlightsRef.current.clear();
    };
  }, [mediaContextKey]);

  const handleSameUrlReload = useCallback((msgId: string) => {
    setReloadKeys(prev => ({ ...prev, [msgId]: (prev[msgId] || 0) + 1 }));
  }, []);

  const handleMediaError = useCallback((messageId: string, failedUrl: string, mode: 'automatic' | 'manual' = 'automatic'): Promise<boolean> => {
    if (!messageId || !recoveryMountedRef.current) return Promise.resolve(false);
    const active = recoveryFlightsRef.current.get(messageId);
    if (active) return active.promise;
    if (mode === 'automatic' && recoveryAttemptsRef.current.has(messageId)) return Promise.resolve(false);
    recoveryAttemptsRef.current.add(messageId);
    const epoch = recoveryEpochRef.current;
    const flight = { promise: Promise.resolve(false) };
    recoveryFlightsRef.current.set(messageId, flight);
    // Registra o dono antes do primeiro await; cliques concorrentes compartilham a mesma operação.
    flight.promise = (async () => {
      try {
        const res = await api.recoverMessageMedia(messageId);
        if (!recoveryMountedRef.current || recoveryEpochRef.current !== epoch) return false;
        if (!res?.success || !res.media_url?.trim()) return false;
        if (res.media_url === failedUrl) handleSameUrlReload(messageId);
        updateMessageInCache(messageId, { media_url: res.media_url });
        return true;
      } catch {
        return false;
      } finally {
        if (recoveryFlightsRef.current.get(messageId) === flight) recoveryFlightsRef.current.delete(messageId);
      }
    })();
    return flight.promise;
  }, [updateMessageInCache, handleSameUrlReload]);

  const handleManualMediaRecovery = useCallback((messageId: string, url: string) =>
    handleMediaError(messageId, url, 'manual'), [handleMediaError]);

  // V7-ALBUM-STABILIZE: enquanto incoming media chega em rajada, renderiza thumbs SOZINHAS.
  // Quando 2s sem incoming media passam (álbum assentou), agrupamento reativa.
  const [isMediaStreaming, setIsMediaStreaming] = useState(false);
  const mediaStreamTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const markMediaStreamActive = useCallback(() => {
    setIsMediaStreaming(true);
    if (mediaStreamTimerRef.current) clearTimeout(mediaStreamTimerRef.current);
    mediaStreamTimerRef.current = setTimeout(() => {
      setIsMediaStreaming(false);
      mediaStreamTimerRef.current = null;
    }, 2000);
  }, []);

  // FIX-E: enquanto um áudio/vídeo estiver tocando (elemento <audio>/<video> ativo),
  // não deixar o groupConsecutiveMedia trocar essa mensagem de individual pra agrupada
  // (ou vice-versa) por baixo do usuário — isso desmontaria o player no meio da
  // reprodução, gatilho conhecido de removeChild em navegadores. Rastreamos por
  // msg.id porque cada mensagem tem no máximo um <audio>/<video> ativo por vez.
  const playingMediaIdsRef = useRef<Set<string>>(new Set());
  const [playingMediaCount, setPlayingMediaCount] = useState(0);
  const setMediaPlaying = useCallback((mediaId: string, isPlaying: boolean) => {
    const set = playingMediaIdsRef.current;
    const had = set.has(mediaId);
    if (isPlaying && !had) {
      set.add(mediaId);
      setPlayingMediaCount(set.size);
    } else if (!isPlaying && had) {
      set.delete(mediaId);
      setPlayingMediaCount(set.size);
    }
  }, []);
  useEffect(() => {
    return () => {
      if (mediaStreamTimerRef.current) clearTimeout(mediaStreamTimerRef.current);
    };
  }, []);

  // V7-ALBUM-STABILIZE: quando álbum assenta (streaming false novamente),
  // atualizar o lead localmente UMA vez com o preview da última mídia ou última mensagem.
  useEffect(() => {
    if (isMediaStreaming) return;
    if (!lead?.id) return;
    if (messages.length === 0) return;
    // só roda em reação à transição true→false
    const last = messages[messages.length - 1];
    if (!last) return;
    const isMedia = ['image','video','audio','document','sticker'].includes(last.message_type);
    const previewMap: Record<string,string> = {
      image: '📷 Imagem',
      video: '🎥 Vídeo',
      audio: '🎵 Áudio',
      document: '📄 Documento',
      sticker: '😊 Sticker'
    };
    const preview = isMedia ? (previewMap[last.message_type] || 'Mídia') : (last.content || 'Mensagem recebida').slice(0, 60);
    updateLeadLocally(lead.id, {
      last_message_preview: preview,
      last_message_at: last.created_at,
      last_message_sender_type: last.message_sender_type,
      ultima_interacao_at: last.created_at,
      unread_count: 0
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediaStreaming]);
  const [isPrivateMode, setIsPrivateMode] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState<'profile' | 'replies' | 'notes' | 'reminders' | 'appointments' | 'actions' | null>(null);
  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  
  const [isActionsRetracted, setIsActionsRetracted] = useState(false);
  const isFloatingActionsExpanded = !isActionsRetracted && !activeDrawer && !leadDrawerOpen;
  
  const toggleDrawer = (drawer: typeof activeDrawer) => {
    if (drawer === 'profile') {
      setLeadDrawerOpen(true);
      setShowMobileActions(false);
      return;
    }
    setActiveDrawer(prev => prev === drawer ? null : drawer);
  };

  const renderMessageTextWithMentions = (text: string) => {
    if (!text) return '';
    const allLeads = useLeadsStore.getState().leads || [];
    const leadsByPhone = new Map<string, string>();
    if (lead?.nome && lead?.telefone) {
      leadsByPhone.set(lead.telefone.replace(/\D/g, ''), lead.nome);
    }
    if (lead?.nome && lead?.remotejid) {
      leadsByPhone.set(lead.remotejid.replace(/\D/g, ''), lead.nome);
    }
    allLeads.forEach(l => {
      if (l.telefone && l.nome) leadsByPhone.set(l.telefone.replace(/\D/g, ''), l.nome);
      if (l.remotejid && l.nome) leadsByPhone.set(l.remotejid.replace(/\D/g, ''), l.nome);
    });

    // U3: Incluir participantes do grupo da conversa ativa a partir das mensagens recentes
    messages.forEach(m => {
      const meta = (m as any)?.metadata;
      if (meta?.participant_phone && meta?.participant_name) {
        const cleanPhone = String(meta.participant_phone).replace(/\D/g, '');
        if (cleanPhone) leadsByPhone.set(cleanPhone, meta.participant_name);
      }
      if (meta?.sender_phone && meta?.sender_name) {
        const cleanPhone = String(meta.sender_phone).replace(/\D/g, '');
        if (cleanPhone) leadsByPhone.set(cleanPhone, meta.sender_name);
      }
    });

    const resolvedText = resolveMentionsInText(text, leadsByPhone);
    return formatMessageText(resolvedText, lead?.nome);
  };

  // U1: Referência e auto-grow dinâmico do composer (estilo WhatsApp Web / Discord)
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);

  const resetTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
  }, []);

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const LIMIT = 50;

  const [recording, setRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [pendingScheduleMessage, setPendingScheduleMessage] = useState<{ message: string, type: 'text' | 'audio', mediaUrl?: string } | null>(null);
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().split('T')[0]);
  const [scheduledTime, setScheduledTime] = useState('08:00');
  const [isScheduling, setIsScheduling] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollLeadIdRef = useRef<string | null>(null);
  const openingScrollRef = useRef(false);
  const sendingRef = useRef(false);

  // Mock typing indicator when lead changes
  useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => setIsTyping(false), 3000);
    return () => clearTimeout(timer);
  }, [lead.id]);

  // Audio Recording Timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (recording) {
      setRecordingDuration(0);
      interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [recording]);

  const formatDuration = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const { cannedResponses, fetchCannedResponses } = useCannedResponsesStore();
  const { lojaAtivaId } = useLojaAtivaStore();

  useEffect(() => {
    fetchCannedResponses(true);
  }, [lojaAtivaId, fetchCannedResponses]);

  const quickRepliesList = useMemo(() => {
    return cannedResponses.map(r => ({
      trigger: r.shortcut,
      text: r.content
    }));
  }, [cannedResponses]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  const handleInputChange = (val: string) => {
    setInput(val);
    adjustTextareaHeight();
    
    if (val.startsWith('/') || val.includes(' /')) {
      setShowAutocomplete(true);
    } else {
      setShowAutocomplete(false);
    }

    // Presence / Typing indicators
    if (!isPrivateMode && activeConnection && lead?.remotejid) {
      const trimmed = val.trim();
      if (trimmed !== '' && !isComposingRef.current) {
        isComposingRef.current = true;
        sendPresence('composing');
      }

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      if (trimmed === '') {
        stopTyping();
      } else {
        typingTimeoutRef.current = setTimeout(() => {
          stopTyping();
        }, 4000);
      }
    }

    // Check for quick replies triggers
    const trigger = quickRepliesList.find(r => safeLower(val).trim() === safeLower(r.trigger));
    if (trigger) {
      handleSend(trigger.text);
    }
  };

  const togglePin = async (msgId: string) => {
    const targetMsg = messages.find(m => m.id === msgId);
    if (!targetMsg) return;
    const newPinned = !targetMsg.is_pinned;
    updateMessageInCache(msgId, { is_pinned: newPinned });
    try {
      await api.updateMessage(msgId, { is_pinned: newPinned });
    } catch (err) {
      updateMessageInCache(msgId, { is_pinned: !newPinned });
      toast.error('Erro ao fixar mensagem');
    }
  };

  const handleDeleteMessage = async (msgId: string) => {
    const targetMsg = messages.find(m => m.id === msgId);
    removeMessageFromCache(msgId);
    try {
      await api.deleteMessage(msgId);
      toast.success('Mensagem excluída');
    } catch (err) {
      if (targetMsg) rollbackDeletedMessage(targetMsg);
      toast.error('Erro ao excluir mensagem');
    }
  };

  const handleReactToMessage = async (msgId: string, emoji: string) => {
    const userId = user?.id || 'atendente';
    const targetMsg = messages.find(m => m.id === msgId);
    if (!targetMsg) return;

    const previousReactions = typeof targetMsg.reactions === 'object' && targetMsg.reactions ? { ...targetMsg.reactions } : {};
    const currentReactions = { ...previousReactions };

    for (const key of Object.keys(currentReactions)) {
      if (Array.isArray(currentReactions[key])) {
        currentReactions[key] = currentReactions[key].filter((id: string) => id !== userId);
        if (currentReactions[key].length === 0) {
          delete currentReactions[key];
        }
      }
    }

    if (emoji) {
      if (!currentReactions[emoji]) {
        currentReactions[emoji] = [];
      }
      if (!currentReactions[emoji].includes(userId)) {
        currentReactions[emoji].push(userId);
      }
    }

    updateMessageInCache(msgId, { reactions: currentReactions });

    try {
      await api.reactToMessage(msgId, emoji);
    } catch (err) {
      console.error('[ReactToMessage] error', err);
      updateMessageInCache(msgId, { reactions: previousReactions });
      toast.error('Erro ao reagir à mensagem');
    }
  };

  const isComposingRef = useRef(false);
  const typingTimeoutRef = useRef<any>(null);

  const activeConnection = useMemo(() => {
    if (lead?.loja_id) {
      const conn = connections.find(c => c.status === 'connected' && c.loja_id === lead.loja_id);
      if (conn) return conn;
    }
    return connections.find(c => c.status === 'connected');
  }, [connections, lead]);

  const sendPresence = async (presence: "composing" | "paused") => {
    if (isPrivateMode) return;
    if (!activeConnection || !lead?.remotejid) return;
    try {
      await api.setPresence(activeConnection.id, lead.remotejid, presence);
    } catch (err) {
      console.error('[sendPresence] error:', err);
    }
  };

  const stopTyping = async () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (isComposingRef.current) {
      isComposingRef.current = false;
      await sendPresence('paused');
    }
  };

  useEffect(() => {
    return () => {
      stopTyping();
    };
  }, [lead?.id]);

  // FIX-E: ao trocar de lead, zera o rastreio de mídia tocando — é uma conversa
  // nova, os ids antigos não fazem mais sentido e não podemos deixar o
  // agrupamento travado por engano numa conversa diferente.
  useEffect(() => {
    playingMediaIdsRef.current.clear();
    setPlayingMediaCount(0);
  }, [lead?.id]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState<'image' | 'video' | 'document' | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  
  // Multiple files/images attachments support
  const [attachedFiles, setAttachedFiles] = useState<{
    id: string;
    file: File;
    previewUrl: string;
    type: 'image' | 'video' | 'document' | 'audio';
    name: string;
    sizeMb: string;
    uploadedUrl?: string;
    isUploading?: boolean;
  }[]>([]);
  const [activeAttachedFileId, setActiveAttachedFileId] = useState<string | null>(null);

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/') || item.type.startsWith('video/') || item.type.includes('pdf')) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    
    if (files.length > 0) {
      e.preventDefault();
      handleMultipleFileUploads(files);
    }
  };

  // Computed attachedFile for any remaining single attachedFile logic
  const attachedFile = useMemo(() => {
    return attachedFiles.find(f => f.id === activeAttachedFileId) || attachedFiles[0] || null;
  }, [attachedFiles, activeAttachedFileId]);

  // Backward compatible setter
  const setAttachedFile = (fileObj: any) => {
    if (fileObj === null) {
      setAttachedFiles([]);
      setActiveAttachedFileId(null);
    } else {
      const fileId = fileObj.id || Math.random().toString(36).substring(7);
      const newObj = { ...fileObj, id: fileId };
      setAttachedFiles([newObj]);
      setActiveAttachedFileId(fileId);
    }
  };

  // Fullscreen Image Viewer with zoom, rotation and history states
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Computed image history of the current chat conversation
  const chatImagesHistory = useMemo(() => {
    const list: { id: string; url: string; date: string }[] = [];
    messages.forEach(msg => {
      if (msg.message_type === 'image' && msg.media_url) {
        list.push({ id: msg.id, url: msg.media_url, date: msg.created_at });
      } else if (msg.content) {
        const match = msg.content.match(/<imagem>(.*?)<\/imagem>/i);
        if (match && match[1]) {
          list.push({ id: msg.id, url: match[1], date: msg.created_at });
        }
      }
    });
    // Deduplicate by URL
    const seen = new Set<string>();
    return list.filter(item => {
      if (!item.url || seen.has(item.url)) return false;
      seen.add(item.url);
      return true;
    });
  }, [messages]);

  const filteredMessages = useMemo(() => {
    const splitMessageIntoVisualBubbles = (msg: Message): Message[] => {
      // FIX: Never split single messages into separate visual bubbles, so signatures and newlines stay in the same message box
      return [msg];
    };

    const base = messages.filter(m => !isTechnicalMessage(m.content, m));
    
    const deduplicated = deduplicateVisualMessages(base);
    const filtered = !searchTerm 
      ? deduplicated 
      : deduplicated.filter(m => safeLower(m.content).includes(safeLower(searchTerm)));

    const finalMessages = filtered.flatMap(msg => splitMessageIntoVisualBubbles(msg));

    return finalMessages;
  }, [messages, searchTerm]);

  // FIX-D: deferir mensagens pra evitar re-agrupamento durante render crítico
  const deferredMessages = useDeferredValue(filteredMessages);

  const chunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    if (!isPrivateMode && !hasConnectedInstance) {
      toast.error('Não é possível gravar áudio porque nenhuma instância do WhatsApp está conectada.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setRecordingStream(stream);
      setRecording(true);
    } catch (err: any) {
      console.error("Audio recording error:", err);
      toast.error("Erro ao acessar o microfone. Verifique as permissões do seu navegador.");
    }
  };

  const finishRecording = (mode: 'send' | 'schedule') => {
    if (!mediaRecorder) return;

    mediaRecorder.onstop = async () => {
      // FIX (S10): usar mimeType real do MediaRecorder (webm na maioria dos browsers)
      const actualMimeType = mediaRecorder.mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: actualMimeType });
      
      try {
        setLoading(true);
        // Extensão baseada no mimeType real
        const ext = actualMimeType.includes('webm') ? 'webm' : actualMimeType.includes('ogg') ? 'ogg' : 'mp3';
        const fileName = `voice-${Date.now()}.${ext}`;
        const file = new File([blob], fileName, { type: actualMimeType });
        const { url } = await api.uploadAudio(file);
        
        if (mode === 'send') {
          handleSend('', 'audio', url);
        } else {
          setPendingScheduleMessage({ message: '', type: 'audio', mediaUrl: url });
          setShowScheduleModal(true);
        }
      } catch (e) {
        toast.error('Erro ao processar áudio');
        console.error(e);
      } finally {
        setLoading(false);
      }
      
      if (recordingStream) {
        recordingStream.getTracks().forEach(track => track.stop());
      }
    };

    mediaRecorder.stop();
    setRecording(false);
    setMediaRecorder(null);
    setRecordingStream(null);
  };

  const cancelRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.onstop = null;
      mediaRecorder.stop();
    }
    if (recordingStream) {
      recordingStream.getTracks().forEach(track => track.stop());
    }
    setRecording(false);
    setMediaRecorder(null);
    setRecordingStream(null);
  };

  const handleScheduleMessage = async () => {
    if (!pendingScheduleMessage) return;
    setIsScheduling(true);
    try {
      // FIX (P8.6): converter pra ISO string com timezone correto
      // new Date("2026-08-01T23:15") interpreta como horário LOCAL do browser
      // .toISOString() converte pra UTC mantendo o horário correto
      const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
      await api.scheduleMessage(
        lead.id,
        lead.remotejid,
        pendingScheduleMessage.message || '',
        scheduledAt,
        pendingScheduleMessage.type,
        pendingScheduleMessage.mediaUrl
      );
      toast.success(`Mensagem agendada com sucesso para ${formatSafe(scheduledAt, 'dd/MM/yyyy HH:mm')}`);
      setShowScheduleModal(false);
      setPendingScheduleMessage(null);
    } catch (e) {
      toast.error('Erro ao agendar mensagem');
    } finally {
      setIsScheduling(false);
    }
  };

  const handleFileAction = (type: 'image' | 'video' | 'document') => {
    setUploadType(type);
    if (fileInputRef.current) {
      if (type === 'image') fileInputRef.current.accept = 'image/*';
      else if (type === 'video') fileInputRef.current.accept = 'video/*';
      else fileInputRef.current.accept = '*/*';
      fileInputRef.current.click();
    }
    setShowAttachMenu(false);
  };

  const getFileTypeFromMime = (mimeType: string): 'image' | 'audio' | 'video' | 'document' => {
    const mime = mimeType.toLowerCase();
    if (mime === 'image/jpeg' || mime === 'image/png' || mime.startsWith('image/')) {
      return 'image';
    }
    if (mime === 'audio/mpeg' || mime === 'audio/ogg' || mime === 'audio/webm' || mime.startsWith('audio/')) {
      return 'audio';
    }
    if (mime === 'video/mp4' || mime.startsWith('video/')) {
      return 'video';
    }
    return 'document';
  };

  const handleFileUpload = async (file: File) => {
    setIsUploadingFile(true);
    // FIX (P7.3): safety timeout — se após 90s o upload ainda não terminou, desligar o spinner
    const safetyTimeoutId = setTimeout(() => {
      setIsUploadingFile(false);
      setAttachedFile(null);
      toast.error('Tempo limite excedido no upload. Tente um arquivo menor.');
    }, 90000);
    
    const fileType = getFileTypeFromMime(file.type);
    const previewUrl = URL.createObjectURL(file);
    const sizeMb = (file.size / (1024 * 1024)).toFixed(2) + ' MB';

    setAttachedFile({
      file,
      previewUrl,
      type: fileType,
      name: file.name,
      sizeMb
    });

    try {
      const uploadResp = await api.uploadFile(file);
      const mediaUrl = uploadResp.publicUrl || uploadResp.url;
      
      if (!mediaUrl) {
        throw new Error('Não foi possível obter a URL do arquivo');
      }

      setAttachedFile(prev => prev ? { ...prev, uploadedUrl: mediaUrl } : null);
      clearTimeout(safetyTimeoutId);
    } catch (err: any) {
      clearTimeout(safetyTimeoutId);
      console.error(err);
      toast.error('Erro ao fazer upload do anexo: ' + (err.message || ''));
      setAttachedFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    } finally {
      clearTimeout(safetyTimeoutId);
      setIsUploadingFile(false);
    }
  };

  const handleMultipleFileUploads = async (files: FileList | File[]) => {
    setIsUploadingFile(true);
    const newAttachedFiles = [...attachedFiles];
    const uploadPromises = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = Math.random().toString(36).substring(7);
      const fileType = getFileTypeFromMime(file.type);
      const previewUrl = URL.createObjectURL(file);
      const sizeMb = (file.size / (1024 * 1024)).toFixed(2) + ' MB';

      const fileObj: any = {
        id: fileId,
        file,
        previewUrl,
        type: fileType,
        name: file.name,
        sizeMb,
        isUploading: true
      };

      newAttachedFiles.push(fileObj);

      const uploadPromise = (async () => {
        try {
          const uploadResp = await api.uploadFile(file);
          const mediaUrl = uploadResp.publicUrl || uploadResp.url;
          if (!mediaUrl) throw new Error('Não foi possível obter a URL do arquivo');
          
          setAttachedFiles(prev => prev.map(f => f.id === fileId ? { ...f, uploadedUrl: mediaUrl, isUploading: false } : f));
        } catch (err: any) {
          console.error(err);
          // Remove the failed file
          setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
          if (previewUrl) URL.revokeObjectURL(previewUrl);
        }
      })();

      uploadPromises.push(uploadPromise);
    }

    setAttachedFiles(newAttachedFiles);
    
    // Set active file
    if (newAttachedFiles.length > 0) {
      const currentlyActiveExists = newAttachedFiles.some(f => f.id === activeAttachedFileId);
      if (!currentlyActiveExists) {
        const newlyAddedFirst = newAttachedFiles[newAttachedFiles.length - files.length];
        if (newlyAddedFirst) {
          setActiveAttachedFileId(newlyAddedFirst.id);
        }
      }
    }

    try {
      await Promise.all(uploadPromises);
    } catch (e) {
      console.error("Error uploading files: ", e);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    handleMultipleFileUploads(files);

    e.target.value = '';
  };

  const historyAnchorReleaseRef = useRef<(() => void) | null>(null);

  const scrollToBottom = () => {
    historyAnchorReleaseRef.current?.();
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const historyAnchorRef = useRef<{
    context: string; firstId: string | undefined; element: HTMLElement | null;
    y: number; height: number; top: number;
  } | null>(null);

  React.useLayoutEffect(() => {
    // A completed request is not a committed deferred message list.
    const anchor = historyAnchorRef.current;
    const el = chatContainerRef.current;
    if (!anchor || !el) return;
    if (anchor.context !== mediaContextKey) { historyAnchorReleaseRef.current?.(); return; }
    if (deferredMessages[0]?.id === anchor.firstId) return;
    // Stop rebasing before our own commit compensation can emit a scroll event.
    historyAnchorReleaseRef.current?.();
    historyAnchorRef.current = anchor;
    const restore = () => {
      if (historyAnchorRef.current !== anchor) return;
      el.scrollTop += anchor.element?.isConnected
        ? anchor.element.getBoundingClientRect().top - anchor.y
        : el.scrollHeight - anchor.height + anchor.top - el.scrollTop;
    };
    restore();
    // Images/typing indicators can settle after insertion. Keep the reader's
    // anchor until their next explicit scroll intent, never pin them to bottom.
    const resize = new ResizeObserver(restore);
    Array.from(el.children).forEach(child => resize.observe(child));
    const release = () => {
      if (historyAnchorRef.current === anchor) historyAnchorRef.current = null;
      if (historyAnchorReleaseRef.current === release) historyAnchorReleaseRef.current = null;
      resize.disconnect();
      ['wheel', 'pointerdown', 'touchstart', 'keydown'].forEach(type => el.removeEventListener(type, release));
    };
    historyAnchorReleaseRef.current = release;
    ['wheel', 'pointerdown', 'touchstart', 'keydown'].forEach(type => el.addEventListener(type, release, { passive: true }));
    return release;
  }, [deferredMessages, mediaContextKey]);

  React.useLayoutEffect(() => () => { historyAnchorReleaseRef.current?.(); }, [mediaContextKey]);

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore) return;
    const el = chatContainerRef.current;
    if (!el) return;
    openingScrollRef.current = false;
    const element = Array.from(el.querySelectorAll<HTMLElement>('[data-message-id]'))
      .find(row => row.getBoundingClientRect().bottom > el.getBoundingClientRect().top) || null;
    const anchor = { context: mediaContextKey, firstId: deferredMessages[0]?.id, element,
      y: element?.getBoundingClientRect().top || 0, height: el.scrollHeight, top: el.scrollTop };
    historyAnchorReleaseRef.current?.();
    historyAnchorRef.current = anchor;
    // While fetch/deferred rendering is pending, preserve the reader's CURRENT
    // position, not the position at click time. Native scrolling stays enabled.
    const rebase = () => {
      if (historyAnchorRef.current !== anchor) return;
      anchor.element = Array.from(el.querySelectorAll<HTMLElement>('[data-message-id]'))
        .find(row => row.getBoundingClientRect().bottom > el.getBoundingClientRect().top) || null;
      anchor.y = anchor.element?.getBoundingClientRect().top || 0;
      anchor.height = el.scrollHeight;
      anchor.top = el.scrollTop;
    };
    const release = () => {
      if (historyAnchorRef.current === anchor) historyAnchorRef.current = null;
      if (historyAnchorReleaseRef.current === release) historyAnchorReleaseRef.current = null;
      el.removeEventListener('scroll', rebase);
    };
    historyAnchorReleaseRef.current = release;
    el.addEventListener('scroll', rebase, { passive: true });
    try {
      await fetchMoreMessages();
    } catch (err) {
      if (historyAnchorRef.current === anchor) historyAnchorReleaseRef.current?.();
      console.error("Failed to load more messages:", err);
    }
  };

  const [showNewMessagesBtn, setShowNewMessagesBtn] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    // New messages button logic
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    if (isAtBottom) {
      setShowNewMessagesBtn(false);
    }
  };

  const hasMessages = messages.length > 0;
  useEffect(() => {
    const el = chatContainerRef.current;
    if (!el || !hasMessages) return;

    // A janela de abertura pertence a conversa, nao a identidade do array.
    // Refetch/realtime nao podem cancelar os retries de layout da abertura.
    openingScrollRef.current = true;
    lastScrollLeadIdRef.current = lead.id;
    const snapToBottom = () => {
      if (openingScrollRef.current) el.scrollTop = el.scrollHeight;
    };
    snapToBottom();
    setShowNewMessagesBtn(false);
    const timers = [60, 200, 500, 1000].map((ms) => setTimeout(snapToBottom, ms));
    // Midia pode ganhar altura muito depois de 1s. Observe somente os filhos
    // deste scroller; nunca ancore novamente depois de intencao de leitura.
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(snapToBottom) : null;
    const observeChildren = () => { resize?.disconnect(); Array.from(el.children).forEach(child => resize?.observe(child)); };
    observeChildren();
    const mutations = typeof MutationObserver !== 'undefined' ? new MutationObserver(() => { observeChildren(); snapToBottom(); }) : null;
    mutations?.observe(el, { childList: true, subtree: true });
    el.addEventListener('load', snapToBottom, true);
    el.addEventListener('loadedmetadata', snapToBottom, true);
    const stopOpening = () => {
      openingScrollRef.current = false;
      resize?.disconnect();
      mutations?.disconnect();
    };
    // Intencao explicita de ler historico vence os retries, sem envio de mensagem.
    el.addEventListener('pointerdown', stopOpening, { passive: true });
    el.addEventListener('wheel', stopOpening, { passive: true });
    el.addEventListener('touchstart', stopOpening, { passive: true });
    el.addEventListener('keydown', stopOpening);
    return () => {
      stopOpening();
      timers.forEach(clearTimeout);
      el.removeEventListener('load', snapToBottom, true);
      el.removeEventListener('loadedmetadata', snapToBottom, true);
      el.removeEventListener('pointerdown', stopOpening);
      el.removeEventListener('wheel', stopOpening);
      el.removeEventListener('touchstart', stopOpening);
      el.removeEventListener('keydown', stopOpening);
    };
  }, [lead.id, hasMessages]);

  useEffect(() => {
    if (!chatContainerRef.current || openingScrollRef.current || lastScrollLeadIdRef.current !== lead.id) return;

    // Conversa ativa: so rola se o usuario ja estava perto do fim (preserva
    // leitura de historico antigo sem pular a tela).
    const { scrollHeight, scrollTop, clientHeight } = chatContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 200;
    if (isAtBottom) {
      scrollToBottom();
    } else {
      setShowNewMessagesBtn(true);
    }
  }, [messages, lead.id]);

  useEffect(() => {
    if (lead?.unread_count && lead.unread_count > 0) {
      const prevUnread = lead.unread_count;
      updateLeadLocally(lead.id, { unread_count: 0 });
      updateConversationLocally(conversation?.id || lead.id, { unread_count: 0 });
      api.markLeadAsRead(lead.id).catch((err: any) => {
        console.warn('[CHAT] Falha ao persistir leitura do lead, revertendo:', err);
        const currentLead = useLeadsStore.getState().leads.find(l => l.id === lead.id);
        if (currentLead && currentLead.unread_count === 0) {
          updateLeadLocally(lead.id, { unread_count: prevUnread });
        }
        const currentConv = useConversationsStore.getState().conversations.find(c => c.lead_id === lead.id || c.id === conversation?.id);
        if (currentConv && currentConv.unread_count === 0) {
          updateConversationLocally(currentConv.id || lead.id, { unread_count: prevUnread });
        }
      });
    }
  }, [lead.id, lead.unread_count, conversation?.id]);

  useEffect(() => {
    if (!supabase || !lead?.id) return;

    let retryCount = 0;
    let retryTimer: any = null;
    let activeChannel: any = null;
    let isAborted = false;

    const setupChannel = () => {
      if (!supabase || isAborted) return;

      activeChannel = supabase
        .channel(`chat:${lead.id}:${Date.now()}`)
        .on(
          'postgres_changes', 
          { 
            event: '*', // Listen for inserts, updates (reactions), and deletes
            schema: 'public', 
            table: MESSAGES_TABLE, 
            filter: `lead_id=eq.${lead.id}` 
          }, 
          payload => {
            if (isAborted) return;
            if (payload.eventType === 'INSERT') {
              const raw = payload.new;
              
              const senderType = raw.message_sender_type || 'lead';
              let content = raw.content || '';
              let msgType = raw.message_type || 'text';
              let mediaUrl = raw.media_url || null;

              if (content) {
                content = cleanMetaText(content);
                const mediaMatch = content.match(/<(audio|imagem|video|pdf)>(.*?)<\/\1>/i);
                if (mediaMatch) {
                   const tagType = safeLower(mediaMatch[1]);
                   msgType = tagType === 'imagem' ? 'image' : (tagType === 'pdf' ? 'document' : tagType);
                   mediaUrl = mediaMatch[2];
                   content = content.replace(mediaMatch[0], '').trim();
                }
                const lowerC = content.toLowerCase().trim();
                if (mediaUrl && (lowerC === '[image]' || lowerC === '[imagem]' || lowerC === '[áudio]' || lowerC === '[audio]' || lowerC === '[vídeo]' || lowerC === '[video]' || lowerC === '📷 imagem')) {
                   content = '';
                }
              }

              // se content estiver vazio e não houver mediaUrl, não adicionar ao state.
              if (!content && !mediaUrl) {
                return;
              }

              // Skip technical system messages that shouldn't appear in chat
              if (senderType === 'sistema' && !content) return;
              
              const mapped: Message = {
                id: String(raw.id),
                tenant_id: lead.tenant_id || 'default',
                conversation_id: raw.conversation_id || 'default',
                lead_id: lead.id,
                message_direction: raw.message_direction || (senderType === 'lead' ? 'inbound' : 'outbound'),
                message_sender_type: senderType,
                message_type: msgType,
                media_url: mediaUrl,
                content: content,
                quoted_message_id: raw.quoted_message_id || undefined,
                quoted_message_content: raw.quoted_message_content || undefined,
                metadata: raw.metadata || {},
                send_status: raw.send_status || 'sent',
                created_at: raw.created_at || raw.timestamp || new Date().toISOString()
              };

              addRealtimeMessage(mapped);

              // V7-ALBUM-STABILIZE: mídia chegando → marcar álbum como "streaming" por 2s
              if (['image','video','audio','document','sticker'].includes(mapped.message_type)) {
                markMediaStreamActive();
              }

              const isTech = isTechnicalMessage(content, mapped);

              // Determine and format a friendly preview
              let friendlyPreview = getCleanedFriendlyPreview(content) || '';

              const msgTime = raw.timestamp || new Date().toISOString();

              // Handle technical messages or media analysis
              const isMediaAnalysis = content && (
                content.includes('ANÁLISE DA MÍDIA ENVIADA') || 
                content.includes('O lead enviou') || 
                content.includes('Lead enviou um') ||
                content.includes('Lead enviou uma')
              );

              if (isTech && isMediaAnalysis) {
                const lower = safeLower(content);
                if (lower.includes('imagem') || lower.includes('legenda')) {
                  friendlyPreview = '📷 Imagem recebida';
                } else if (lower.includes('áudio') || lower.includes('audio')) {
                  friendlyPreview = '🎵 Áudio recebido';
                } else if (lower.includes('vídeo') || lower.includes('video')) {
                  friendlyPreview = '🎥 Vídeo recebido';
                } else {
                  friendlyPreview = '📄 Documento recebido';
                }
              }

              if (!friendlyPreview) {
                friendlyPreview = 'Mensagem recebida';
              }

              // V7-ALBUM-STABILIZE: durante álbum stream, NÃO chamamos updateLeadLocally aqui.
              // O markLeadAsRead abaixo já zera o unread; o último updateLeadLocally dispara
              // automaticamente quando isMediaStreaming volta a false (debounce de 2s).
              const mappedIsMedia2 = ['image','video','audio','document','sticker'].includes(mapped.message_type);
              if (mappedIsMedia2) {
                // álbum em stream — não tocar na sidebar agora
              } else if (!isTech || isMediaAnalysis) {
                updateLeadLocally(lead.id, {
                  last_message_preview: friendlyPreview,
                  last_message_at: msgTime,
                  last_message_sender_type: senderType,
                  ultima_interacao_at: msgTime,
                  unread_count: 0
                });
              } else {
                updateLeadLocally(lead.id, {
                  unread_count: 0
                });
              }

              if (senderType === 'lead') {
                api.markLeadAsRead(lead.id).catch((err: any) => {
                  console.warn('[CHAT] Falha ao marcar lead como lido no evento de mensagem:', err);
                });
              }
            } else if (payload.eventType === 'DELETE') {
              removeMessageFromCache(String(payload.old.id));
            } else if (payload.eventType === 'UPDATE') {
              const updated = payload.new;
              let content = updated.content || '';
              let msgType = updated.message_type;
              let mediaUrl = updated.media_url;

              if (content) {
                content = cleanMetaText(content);
                const mediaMatch = content.match(/<(audio|imagem|video|pdf)>(.*?)<\/\1>/i);
                if (mediaMatch) {
                   const tagType = safeLower(mediaMatch[1]);
                   msgType = tagType === 'imagem' ? 'image' : (tagType === 'pdf' ? 'document' : tagType);
                   mediaUrl = mediaMatch[2];
                   content = content.replace(mediaMatch[0], '').trim();
                }
              }

              updateMessageInCache(String(updated.id), {
                ...updated,
                id: String(updated.id),
                content,
                media_url: mediaUrl,
                message_type: msgType,
                reactions: updated.reactions,
                is_deleted: updated.is_deleted
              });
            }
          }
        )
        .subscribe((status) => {
          if (isAborted) return;
          console.log(`[CHAT REALTIME] Status lead ${lead.id}:`, status);
          if (status === 'SUBSCRIBED') {
            const needsSync = retryCount > 0 || openingHistoryRef.current.needsSync;
            openingHistoryRef.current.needsSync = false;
            // Cold open already has its GET; don't duplicate that request. A
            // warm reopen must not wait up to staleTime for missed messages.
            if (needsSync && !messagesFetchingRef.current) {
              void refetchMessages();
            }
            retryCount = 0;
            if (lead.unread_count && lead.unread_count > 0) {
              const prevUnread = lead.unread_count;
              updateLeadLocally(lead.id, { unread_count: 0 });
              updateConversationLocally(conversation?.id || lead.id, { unread_count: 0 });
              api.markLeadAsRead(lead.id).catch((err: any) => {
                console.warn('[CHAT] Falha ao marcar como lido no status SUBSCRIBED:', err);
                const currentLead = useLeadsStore.getState().leads.find(l => l.id === lead.id);
                if (currentLead && currentLead.unread_count === 0) {
                  updateLeadLocally(lead.id, { unread_count: prevUnread });
                }
                const currentConv = useConversationsStore.getState().conversations.find(c => c.lead_id === lead.id || c.id === conversation?.id);
                if (currentConv && currentConv.unread_count === 0) {
                  updateConversationLocally(currentConv.id || lead.id, { unread_count: prevUnread });
                }
              });
            }
          } else if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && retryCount < 5) {
            console.warn(`[CHAT REALTIME] Canal caiu (status=${status}), reconectando...`);
            const delay = Math.min(2000 * Math.pow(2, retryCount), 60000);
            retryCount++;
            retryTimer = setTimeout(() => {
              if (isAborted) return;
              if (activeChannel) {
                try { supabase.removeChannel(activeChannel); } catch {}
              }
              setupChannel();
            }, delay);
          }
        });
    };

    setupChannel();

    return () => {
      isAborted = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (activeChannel) {
        try { supabase.removeChannel(activeChannel); } catch {}
      }
    };
  }, [lead.id, lead.remotejid, addRealtimeMessage, removeMessageFromCache, updateMessageInCache, refetchMessages]);

  // Reminders are presented only by Layout's recipient-scoped /api/my-notifications feed.
  // The lead broadcast is not recipient-scoped and must not produce a second/potentially foreign alert.

  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{ text: string, sender: string, msgId?: string } | null>(null);

  const handleSend = async (customContent?: string, type: Message['message_type'] = 'text', mediaUrl?: string, retryOperationId?: string) => {
    // FIX (P4.3): prevenir múltiplos envios simultâneos
    if (sendingRef.current) return;
    
    stopTyping();
    const command = (customContent ?? input).trim().toLowerCase();
    if (!isPrivateMode && type === 'text' && !mediaUrl && attachedFiles.length === 0 &&
        ['/reset', '/resume', '/pause'].includes(command)) {
      if (!conversation?.id) {
        toast.error('Aguarde o carregamento da conversa para controlar a IA.');
        return;
      }
      setLoading(true);
      sendingRef.current = true;
      try {
        const result = await api.controlConversationAi(conversation.id, command.slice(1) as 'reset' | 'resume' | 'pause');
        if (result.success !== true) throw new Error('Controle da IA não confirmado');
        setInput('');
        setReplyingTo(null);
        // An authenticated local command is NOT a sent WhatsApp bubble.
        if (result.confirmation) toast.success(result.confirmation, { duration: 10000 });
        else toast.success('Este comando já foi processado.');
        await queryClient.invalidateQueries({ queryKey: ['conversations'] });
        await queryClient.invalidateQueries({ queryKey: ['leads'] });
      } catch (error: any) {
        toast.error(`Comando não confirmado: ${error.message || 'tente novamente após verificar a conversa'}`);
      } finally {
        setLoading(false);
        sendingRef.current = false;
      }
      return;
    }
    if (!isPrivateMode && !hasConnectedInstance) {
      toast.error('Nenhuma instância do WhatsApp está conectada. Conecte uma instância nas configurações ou utilize o Modo Nota Interna para salvar anotações.');
      return;
    }

    let textToSend = customContent !== undefined ? customContent : input;
    let finalType = type;
    let finalMediaUrl = mediaUrl;

    if (attachedFiles.length > 0) {
      setLoading(true);
      sendingRef.current = true;
      const currentQuote = replyingTo;

      if (currentQuote) {
        setReplyingTo(null);
      }

      try {
        for (let i = 0; i < attachedFiles.length; i++) {
          const fileObj = attachedFiles[i];
          let fileMediaUrl = fileObj.uploadedUrl;
          let fileType = fileObj.type;

          if (!fileMediaUrl) {
            if (fileObj.type === 'audio') {
              const res = await api.uploadAudio(fileObj.file);
              fileMediaUrl = res.url;
            } else {
              const res = await api.uploadFile(fileObj.file);
              fileMediaUrl = res.publicUrl || res.url;
            }
          }

          // Revoke preview object URL
          if (fileObj.previewUrl) URL.revokeObjectURL(fileObj.previewUrl);

          const currentCaption = i === 0 ? textToSend : '';

          // Optimistic UI for each file
          const optimisticMsg: Message = {
            id: retryOperationId || crypto.randomUUID(),
            tenant_id: lead.tenant_id,
            conversation_id: 'pending',
            lead_id: lead.id,
            message_direction: 'outbound',
            message_sender_type: 'humano',
            message_type: fileType,
            content: currentCaption,
            media_url: fileMediaUrl,
            quoted_message_id: currentQuote?.msgId,
            quoted_message_content: currentQuote?.text,
            send_status: 'pending',
            created_at: new Date().toISOString()
          };
          addOptimisticMessage(optimisticMsg);

          // Update lead locally
          updateLeadLocally(lead.id, {
            last_message_preview: fileType === 'image' ? '📷 Imagem' : fileType === 'audio' ? '🎵 Áudio' : fileType === 'video' ? '🎥 Vídeo' : '📄 Documento',
            last_message_at: new Date().toISOString(),
            last_message_sender_type: 'humano',
            ultima_interacao_at: new Date().toISOString()
          });

          try {
            const realMsg = await api.sendMessage(
              lead.id,
              currentCaption,
              lead.remotejid,
              fileType,
              fileMediaUrl,
              currentQuote?.msgId,
              currentQuote?.text,
              isPrivateMode,
              optimisticMsg.id
            );

            replaceOptimisticWithReal(optimisticMsg.id, realMsg);
            // The sidebar reads the conversation cache, not the optimistic lead preview.
            if (!isPrivateMode && ['sent', 'delivered', 'read'].includes(realMsg.send_status || '')) {
              void queryClient.invalidateQueries({ queryKey: ['conversations'] });
            }
          } catch (sendErr: any) {
            console.error('[CHAT] Erro ao enviar anexo:', sendErr);
            toast.error(`Falha ao enviar anexo ${fileObj.name || i + 1}`);
            markOptimisticFailed(optimisticMsg.id, sendErr?.message || 'Erro ao enviar anexo');
          }
        }

        if (customContent === undefined) {
          setInput('');
          resetTextareaHeight();
        }
        setAttachedFiles([]);
        setActiveAttachedFileId(null);
        setLoading(false);
        sendingRef.current = false;
        return;
      } catch (err: any) {
        console.error(err);
        toast.error('Erro ao enviar um ou mais anexos');
        setLoading(false);
        sendingRef.current = false;
        return;
      }
    }

    if (!textToSend.trim() && !finalMediaUrl) {
      setLoading(false);
      sendingRef.current = false;
      return;
    }

    sendingRef.current = true;
    setLoading(true); // Desabilitar input durante envio
    const currentQuote = replyingTo;

    if (customContent === undefined) {
      setInput('');
      resetTextareaHeight();
    }

    if (currentQuote) {
      setReplyingTo(null);
    }
    
    // Optimistic UI
    const optimisticMsg: Message = {
      id: retryOperationId || crypto.randomUUID(),
      tenant_id: lead.tenant_id,
      conversation_id: 'pending',
      lead_id: lead.id,
      message_direction: 'outbound',
      message_sender_type: 'humano',
      message_type: finalType,
      content: textToSend,
      media_url: finalMediaUrl,
      quoted_message_id: currentQuote?.msgId,
      quoted_message_content: currentQuote?.text,
      send_status: 'pending',
      created_at: new Date().toISOString()
    };
    addOptimisticMessage(optimisticMsg);
    
    // Optmistic Update on Store for Preview
    updateLeadLocally(lead.id, {
      last_message_preview: finalType === 'text' ? textToSend : (finalType === 'image' ? '📷 Imagem' : finalType === 'audio' ? '🎵 Áudio' : finalType === 'video' ? '🎥 Vídeo' : '📄 Documento'),
      last_message_at: new Date().toISOString(),
      last_message_sender_type: 'humano',
      ultima_interacao_at: new Date().toISOString()
    });
    
    try {
      const realMsg = await api.sendMessage(
        lead.id, 
        textToSend, 
        lead.remotejid, 
        finalType, 
        finalMediaUrl, 
        currentQuote?.msgId, 
        currentQuote?.text,
        isPrivateMode,
        optimisticMsg.id
      );
      replaceOptimisticWithReal(optimisticMsg.id, realMsg);
      // The sidebar reads conversation queries, not leadsStore; recover authoritatively
      // after a confirmed public send even if the realtime event is delayed or lost.
      if (!isPrivateMode && ['sent', 'delivered', 'read'].includes(realMsg.send_status || '')) {
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
      if (realMsg.send_status === 'failed') {
        toast.error(`Falha ao enviar pelo WhatsApp: ${realMsg.error || 'Erro desconhecido'}`);
      } else if (isPrivateMode) {
        toast.success('Nota interna salva');
        setIsPrivateMode(false);
      }
    } catch (e: any) {
      console.error('[CHAT] Erro ao enviar:', e);
      const errMsg = e.message || 'Erro ao enviar mensagem';
      toast.error('Resultado não confirmado. Não envie como nova mensagem; revise o histórico.');
      markOptimisticFailed(optimisticMsg.id, errMsg);
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  const templates = [
    "Olá! Como posso ajudar hoje?",
    "Agradecemos o contato. Em breve retornaremos.",
    "Poderia me confirmar o seu email?",
    "A proposta foi enviada para o seu email.",
  ];

  return (
    <div className="flex flex-col h-full bg-[#f9f9f9] dark:bg-[#0B141A] relative overflow-hidden transition-colors duration-300">
      {/* New Messages Indicator */}
      {showNewMessagesBtn && (
        <button 
          onClick={scrollToBottom}
          className="absolute bottom-24 right-8 bg-white dark:bg-[#202C33] text-blue-600 dark:text-blue-400 px-4 py-2 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 z-50 flex items-center gap-2 animate-bounce cursor-pointer"
        >
          <MessageSquare size={16} />
          <span className="text-xs font-bold">Novas mensagens</span>
        </button>
      )}

      {/* Header (wa-ui) */}
      <div className="relative flex-shrink-0 z-20">
        <ChatHeader
          name={lead.nome || lead.telefone || 'Sem nome'}
          avatar={lead.avatar_url}
          onAvatarClick={() => toggleDrawer('profile')}
          onTitleClick={() => toggleDrawer('profile')}
          onBack={onBack}
          onSearch={() => setShowSearch(!showSearch)}
          status={
            <div className="flex items-center gap-1.5 min-w-0">
              {((conversation as any)?.ai_typing && (conversation as any)?.ai_typing_at && new Date((conversation as any).ai_typing_at).getTime() > Date.now() - 30000) ? (
                <span className="text-[10px] md:text-[11px] font-bold text-blue-500 dark:text-blue-400 italic tracking-wider animate-pulse flex items-center gap-1 leading-none">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-ping" />
                  Bia está digitando...
                </span>
              ) : (isTyping || lead.is_typing) ? (
                <span className="text-[10px] md:text-[11px] font-bold text-emerald-500 dark:text-emerald-400 uppercase tracking-wider animate-pulse flex items-center gap-1 leading-none">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                  digitando...
                </span>
              ) : (
                <span className="text-[10px] md:text-[11px] text-wa-header-subtitle truncate leading-none">
                  {lead.telefone ? `${lead.telefone} • ` : ''}{lead.status_atendimento || 'ATENDIMENTO'}
                </span>
              )}

              {conversation?.assigned_user_id && (
                <span className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 font-medium truncate">
                  <UserCheck size={11} className="shrink-0" />
                  <span className="truncate">
                    {isMyConversation 
                      ? 'Você' 
                      : `${conversation.assigned_user_name || assignedUserName || 'Atendente'}`}
                  </span>
                </span>
              )}
            </div>
          }
          customActions={
            <div className="flex items-center gap-1.5 md:gap-2">
              {/* Status & Priority Controls */}
              {conversation && (
                <div className="hidden sm:flex items-center gap-1.5 mr-1">
                  {/* Prioridade: exibida apenas no dropdown abaixo (badge duplicado removido) */}

                  {/* Badge de status (já existe, manter) */}
                  {conversation.status === 'resolved' && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      ✓ Resolvido
                    </span>
                  )}

                  <select
                    value={conversation.status || 'open'}
                    onChange={(e) => {
                      const newStatus = e.target.value as any;
                      updateStatus(conversation.id, newStatus);
                      toast.success(`Status alterado para ${newStatus}`);
                    }}
                    className={cn(
                      "text-[11px] font-bold px-2 py-1 rounded-lg border border-transparent outline-none cursor-pointer transition-colors",
                      conversation.status === 'open' && "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
                      conversation.status === 'resolved' && "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
                      conversation.status === 'pending' && "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
                      conversation.status === 'snoozed' && "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300"
                    )}
                  >
                    <option value="open">Aberto</option>
                    <option value="pending">Pendente</option>
                    <option value="snoozed">Adiado</option>
                    <option value="resolved">Resolvido</option>
                  </select>

                  <select
                    value={conversation.priority || 'medium'}
                    onChange={(e) => {
                      const newPriority = e.target.value as any;
                      updatePriority(conversation.id, newPriority);
                      toast.success(`Prioridade alterada`);
                    }}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700 outline-none cursor-pointer"
                  >
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>

                  {conversation.status !== 'resolved' && (
                    <button
                      onClick={async () => {
                        await resolveConversation(conversation.id);
                        toast.success('Conversa resolvida');
                      }}
                      className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95"
                      title="Marcar como Resolvido"
                    >
                      <CheckCircle size={14} />
                      <span>Resolver</span>
                    </button>
                  )}
                </div>
              )}

              {/* Indicador visual de resultado do aviso de grupo */}
              {(() => {
                const notice = (conversation as any)?.metadata?.ai_control?.last_group_notice;
                if (!notice || !notice.result) return null;
                let text = '';
                let colorClass = 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
                if (notice.result === 'sent') {
                  const timeStr = notice.at ? new Date(notice.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                  text = `Aviso grupo: enviado às ${timeStr}`;
                  colorClass = 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60';
                } else if (notice.result === 'skipped') {
                  text = 'Aviso grupo: pulado';
                  colorClass = 'text-gray-600 bg-gray-100 dark:bg-gray-800 dark:text-gray-400 border-gray-200 dark:border-gray-700';
                } else if (notice.result === 'failed') {
                  text = 'Aviso grupo: falha no aviso — verificar grupo';
                  colorClass = 'text-red-700 bg-red-50 dark:bg-red-950/40 dark:text-red-300 border-red-200 dark:border-red-800/60';
                }
                if (!text) return null;
                return (
                  <span
                    className={cn(
                      "hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border transition-colors",
                      colorClass
                    )}
                    title={notice.destination ? `Destino: ${notice.destination}` : undefined}
                  >
                    {text}
                  </span>
                );
              })()}

              {/* Reativar IA Button (só se conversa está atribuída a humano E loja tem IA ativa) */}
              {conversation && conversation.assigned_user_id && conversation.ai_active && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleReactivateAI(); }}
                  className="p-1.5 w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors shrink-0 cursor-pointer dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                  title="IA pausada (humano assumiu). Clique duas vezes para reativar a IA."
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="10" rx="2" />
                    <circle cx="12" cy="5" r="2" />
                    <path d="M12 7v4" />
                    <line x1="8" y1="16" x2="8" y2="16" />
                    <line x1="16" y1="16" x2="16" y2="16" />
                  </svg>
                </button>
              )}

              {/* Assumir / Liberar Button */}
              {conversation && (
                isMyConversation ? (
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleRelease(); }} 
                    className="text-xs px-2.5 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold transition-all shadow-xs shrink-0 cursor-pointer"
                  >
                    Liberar
                  </button>
                ) : (
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleAssign(); }} 
                    className="text-xs px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold transition-all shadow-xs shrink-0 cursor-pointer"
                  >
                    Assumir
                  </button>
                )
              )}

              {/* Hidden Settings button on mobile header */}
              <button 
                onClick={() => toggleDrawer('actions')}
                className="hidden md:block p-2 text-wa-icon-lighter hover:text-wa-icon hover:bg-wa-hover rounded-full transition-all shrink-0"
                title="Ações"
              >
                <Settings2 size={20} />
              </button>
            </div>
          }
        />

        {/* Search Bar Overlay */}
        {showSearch && (
          <div className="absolute inset-x-0 top-0 h-full bg-[#f0f2f5] dark:bg-[#202C33] px-4 flex items-center gap-4 z-50 animate-in slide-in-from-top-4 duration-200">
            <button onClick={() => { setShowSearch(false); setSearchTerm(''); }} className="p-2 text-[#54656f] dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full">
              <X size={20} />
            </button>
            <div className="flex-1 bg-white dark:bg-[#2A3942] rounded-lg flex items-center px-4 py-1.5 border border-gray-200 dark:border-gray-700 focus-within:ring-1 focus-within:ring-emerald-500 transition-colors">
               <input 
                 autoFocus
                 type="text" 
                 value={searchTerm}
                 onChange={e => setSearchTerm(e.target.value)}
                 placeholder="Pesquisar mensagens"
                 className="w-full bg-transparent outline-none text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
               />
            </div>
          </div>
        )}
      </div>

      {/* SLA / Transbordo Warning Banner */}
      {conversation?.sla_exceeded && (
        <div className="bg-rose-50 dark:bg-rose-950/20 border-b border-rose-100 dark:border-rose-900/30 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-in slide-in-from-top-2 duration-350 z-20">
          <div className="flex items-start sm:items-center gap-2.5">
            <div className="p-2 bg-rose-500 text-white rounded-full shrink-0">
              <ShieldAlert size={18} className="animate-pulse" />
            </div>
            <div>
              <p className="text-[13px] font-bold text-rose-900 dark:text-rose-200">
                Alerta de Transbordo (SLA Estourado)
              </p>
              <p className="text-[11.5px] text-rose-700 dark:text-rose-450 font-medium">
                Esta conversa ultrapassou o tempo limite de resposta de SLA definido pela loja. Qualquer vendedor pode assumir o atendimento.
              </p>
            </div>
          </div>
          <button
            onClick={handleAssume}
            className="self-start sm:self-center bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-bold text-xs px-3.5 py-1.5 rounded-lg shadow-sm transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5"
          >
            <UserCheck size={14} />
            <span>Assumir Atendimento</span>
          </button>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden relative">
        {/* Floating Retractable Bubbles */}
        {/* FIX-B: container externo SEMPRE montado — condição de visibilidade
            movida PARA DENTRO do AnimatePresence pra evitar que o React
            desmonte o pai enquanto o Framer Motion ainda gerencia a
            animação de saída (causa raiz do removeChild). */}
        <div className="absolute left-4 top-4 bottom-4 hidden md:flex flex-col items-center pointer-events-none z-[60] animate-in fade-in duration-300">
          {(!activeDrawer && !leadDrawerOpen) && (
            <button 
              onClick={() => setIsActionsRetracted(!isActionsRetracted)}
              className="p-2 mb-4 bg-white dark:bg-[#202C33] rounded-full shadow-lg border border-gray-200 dark:border-gray-800 text-gray-500 hover:text-blue-600 transition-all active:scale-90 pointer-events-auto ring-1 ring-black/5"
              title={isActionsRetracted ? "Expandir Ações" : "Recolher Ações"}
            >
              {isActionsRetracted ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          )}

          <AnimatePresence mode="wait">
            {!isActionsRetracted && !activeDrawer && !leadDrawerOpen && (
              <motion.div 
                key="floating-actions"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -20, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-3 pointer-events-auto"
              >
                {[
                  { id: 'profile', icon: UserRound, label: 'Perfil', color: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400' },
                  { id: 'replies', icon: Zap, label: 'Respostas Rápidas', color: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' },
                  { id: 'notes', icon: StickyNote, label: 'Anotações', color: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
                  { id: 'reminders', icon: Bell, label: 'Lembretes', color: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400' },
                  { id: 'appointments', icon: CalendarClock, label: 'Agendamentos', color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
                  { id: 'actions', icon: Settings2, label: 'Ações', color: 'bg-gray-50 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400' },
                ].map((item) => (
                  <button 
                    key={item.id}
                    onClick={() => toggleDrawer(item.id as any)}
                    className={cn(
                      "w-12 h-12 rounded-full shadow-lg border flex items-center justify-center transition-all relative group shrink-0",
                      activeDrawer === item.id 
                        ? `${item.color} border-blue-500 ring-2 ring-blue-500/20` 
                        : "bg-white dark:bg-[#202C33] text-gray-400 border-gray-200 dark:border-gray-800 hover:scale-110 active:scale-95"
                    )}
                  >
                    <item.icon size={20} strokeWidth={activeDrawer === item.id ? 2.5 : 2} />
                    
                    {/* Tooltip */}
                    <div className="absolute left-full ml-4 px-3 py-1.5 bg-gray-900 text-white text-[11px] rounded-lg pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-[70] font-bold tracking-wider shadow-xl border border-white/10 uppercase">
                      {item.label}
                    </div>

                    {/* Active Indicator Dot (estático, sem layoutId) */}
                    {activeDrawer === item.id && (
                      <div 
                        className="absolute -right-1 -top-1 w-3 h-3 bg-blue-600 rounded-full border-2 border-white dark:border-[#202C33]" 
                      />
                    )}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Chat Content */}
        <div className="flex-1 flex flex-col min-w-0 whatsapp-chat-bg transition-all duration-300 overflow-hidden">
          {/* Messages */}
          <div 
            ref={chatContainerRef}
            onScroll={handleScroll}
            className={cn(
              "flex-1 overflow-y-auto px-3 md:px-4 py-6 space-y-2 no-scrollbar",
              isFloatingActionsExpanded && "md:pl-20"
            )}
          >
        {messagesLoading && messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-500 dark:text-gray-400">
             <div className="w-12 h-12 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
             <p className="text-sm font-bold uppercase tracking-widest animate-pulse">Carregando Histórico...</p>
          </div>
        ) : messagesError && messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-white/40 dark:bg-black/20 backdrop-blur-sm rounded-3xl mx-4 my-2 border-2 border-red-200 dark:border-red-900/30 transition-colors">
             <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8" />
             </div>
             <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1">Não foi possível carregar o histórico</h3>
             <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs mb-4">Ocorreu um erro ao carregar as mensagens deste contato.</p>
             <button
               type="button"
               onClick={() => refetchMessages()}
               className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors cursor-pointer"
             >
               Tentar novamente
             </button>
          </div>
        ) : !messagesLoading && !messagesError && messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8 bg-white/40 dark:bg-black/20 backdrop-blur-sm rounded-3xl mx-4 my-2 border-dashed border-2 border-gray-200 dark:border-gray-800 transition-colors">
             <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
                <MessageSquare className="text-gray-300 dark:text-gray-600 w-10 h-10" />
             </div>
             <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Sem histórico ainda</h3>
             <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">Nenhuma mensagem encontrada para este contato. Inicie uma conversa abaixo!</p>
          </div>
        ) : (
          <MessageListErrorBoundary
            key={messageListResetKey}
            onReset={() => setMessageListResetKey(k => k + 1)}
          >
            {messagesError && messages.length > 0 && (
              <div className="mx-auto max-w-[90%] mb-4 p-2.5 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg flex items-center justify-between text-xs text-red-700 dark:text-red-300 shadow-sm">
                <div className="flex items-center gap-2">
                  <AlertCircle size={15} className="shrink-0 text-red-500" />
                  <span>Erro ao sincronizar mensagens recentes.</span>
                </div>
                <button
                  type="button"
                  onClick={() => refetchMessages()}
                  className="font-bold underline hover:no-underline ml-2 cursor-pointer"
                >
                  Tentar novamente
                </button>
              </div>
            )}
            {hasMore && (
              <div className="flex justify-center py-2 pb-3">
                <button
                  type="button"
                  onClick={loadMoreMessages}
                  disabled={loadingMore}
                  className="flex items-center gap-1.5 px-3 py-1 bg-white dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 text-xs font-bold text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A3942] transition-colors shadow-sm disabled:opacity-60 cursor-pointer"
                >
                  {loadingMore ? (
                    <>
                      <span className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">Carregando...</span>
                    </>
                  ) : (
                    <span className="text-[11px]">Carregar mensagens anteriores</span>
                  )}
                </button>
              </div>
            )}
            {/* Dates belong to message-day boundaries, never to the current wall clock. */}
            
            {/* Pinned Messages Header */}
            {messages.some(m => m.is_pinned) && (
              <div className="sticky top-12 z-20 mx-auto max-w-[90%] bg-white/90 dark:bg-[#202C33]/90 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-2 flex items-center gap-3 shadow-md animate-in slide-in-from-top-4 duration-300 mb-4 transition-colors">
                 <div className="bg-emerald-100 dark:bg-emerald-900/30 p-1.5 rounded-full text-emerald-600 dark:text-emerald-400">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 9V4l1 0V2H7v2l1 0v5c0 2.18-1.79 4-4 4v2h7v9l1 1 1-1v-9h7v-2c-2.21 0-4-1.82-4-4z"/></svg>
                 </div>
                 <div className="flex-1 overflow-hidden">
                    <p className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 leading-none mb-1 uppercase tracking-wider">Mensagem Fixada</p>
                    <p className="text-[12px] text-gray-700 dark:text-gray-200 truncate">{safeText(messages.find(m => m.is_pinned)?.content).replace(/^\*([^*]+)\*(?:\s*\r?\n\s*|\s+)/, '')}</p>
                 </div>
                 <button onClick={() => togglePin(messages.find(m => m.is_pinned)!.id)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1">
                    <X size={14} />
                 </button>
              </div>
            )}

            {(() => {
              // V7-ALBUM-STABILIZE: durante incoming media, renderiza thumbs SOZINHAS.
              // Álbum assentou (2s sem incoming)? Agrupa de uma vez.
              const grouped = (isMediaStreaming || playingMediaCount > 0)
                ? deferredMessages.map(m => ({ ...m, is_media_group: false, messages: undefined }))
                : groupConsecutiveMedia(deferredMessages);
              return grouped.flatMap((msg, msgIndex) => {
          const isInbound = msg.message_direction === 'inbound';
          const isIA = msg.message_sender_type === 'ia';

          // Date separator logic
          const msgDate = new Date(msg.created_at);
          const prevMsg = msgIndex > 0 ? grouped[msgIndex - 1] : null;
          const prevMsgDate = prevMsg ? new Date(prevMsg.created_at) : null;
          
          const isDifferentDay = !prevMsgDate || 
            msgDate.toLocaleDateString() !== prevMsgDate.toLocaleDateString();

          const dateLabel = () => {
            const today = new Date();
            const yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);

            if (msgDate.toLocaleDateString() === today.toLocaleDateString()) return 'Hoje';
            if (msgDate.toLocaleDateString() === yesterday.toLocaleDateString()) return 'Ontem';
            return msgDate.toLocaleDateString('pt-BR');
          };

          const dateSeparator = isDifferentDay ? (
            // Keep separators in flow: sibling sticky dates share one containing block
            // and accumulate at top:0 when reading older days.
            <DateSeparator label={dateLabel()} key={`date-${msg.id}`} className="my-4" data-message-date={msgDate.toLocaleDateString('pt-BR')} />
          ) : null;
          
          type MessagePart = {
             id: string;
             type: 'audio'|'image'|'video'|'document'|'text'|'media_group_grid';
             value: string;
             quote?: string;
             messages?: Message[];
          };

          const parts: MessagePart[] = [];
          let index = 0;
          let text = safeText(msg.content);
          let extractedSenderSignature: string | null = null;
          
          // Pattern: ^*Name* followed by optional whitespace and newlines
          const signatureRegex = /^\*([^*]+)\*(?:\s*\r?\n\s*|\s+)/;
          const sigMatch = text.match(signatureRegex);
          if (sigMatch) {
            extractedSenderSignature = sigMatch[1].trim();
            text = text.replace(signatureRegex, '').trim();
          }

          const getSignatureOfMessage = (m: Message | null): string | null => {
            if (!m) return null;
            const content = safeText(m.content);
            const match = content.match(/^\*([^*]+)\*(?:\s*\r?\n\s*|\s+)/);
            return match ? match[1].trim() : null;
          };
          const prevSignature = getSignatureOfMessage(prevMsg);
          
          const MEDIA_PLACEHOLDERS = ['[imagem]', '[imagem]', '[áudio]', '[audio]', '[vídeo]', '[video]', '[documento]', '[sticker]', '[mídia]', '[midia]'];

          if (msg.is_media_group && msg.messages) {
            parts.push({
              id: `${msg.id}-grid`,
              type: 'media_group_grid',
              value: '',
              messages: msg.messages
            });
            
            for (const m of msg.messages) {
              const mText = safeText(m.content);
              const isOnlyPlaceholder = MEDIA_PLACEHOLDERS.includes(mText.trim().toLowerCase());
              if (mText.trim() && !isOnlyPlaceholder) {
                parts.push({
                  id: `${m.id}-caption`,
                  type: 'text',
                  value: mText
                });
              }
            }
          } else {
            const regex = /(<(?:audio|imagem|video|pdf|documento)>.*?<\/(?:audio|imagem|video|pdf|documento)>)/gi;

            // O mesmo ramo que cria a parte de mídia define quem é dono da legenda.
            let nativeMediaType: 'image' | 'video' | null = null;
            if (msg.message_type !== 'text' && !text.match(regex)) {
               let normType: 'audio' | 'image' | 'video' | 'document' | 'text' = 'document';
               if (msg.message_type === 'image' || msg.message_type === 'sticker') normType = 'image';
               else if (msg.message_type === 'audio' || msg.message_type === 'ptt' || msg.message_type === 'voice') normType = 'audio';
               else if (msg.message_type === 'video') normType = 'video';
               else if (msg.message_type === 'document' || msg.message_type === 'media') normType = 'document';
               if (normType === 'image' || normType === 'video') nativeMediaType = normType;
               parts.push({ id: `${msg.id}-${index++}`, type: normType, value: msg.media_url || '' });
            }
            
            const isOnlyPlaceholder = MEDIA_PLACEHOLDERS.includes(text.trim().toLowerCase());
            // A legenda nativa já é desenhada pelo componente de mídia; criar também uma
            // bolha de texto repetiria o mesmo conteúdo. Áudio, documento e mídia mista
            // continuam com o texto separado.
            const nativeCaption = nativeMediaType !== null;
            const textToProcess = isOnlyPlaceholder || nativeCaption ? '' : text;

            const tokens = textToProcess.split(regex);
            for (const token of tokens) {
              if (!token) continue;
              const mediaMatch = token.match(/<(audio|imagem|video|pdf|documento)>(.*?)<\/\1>/i);
              if (mediaMatch) {
                 const tag = safeLower(mediaMatch[1]);
                 const url = mediaMatch[2];
                 const typeMap: Record<string, any> = { imagem: 'image', audio: 'audio', video: 'video', pdf: 'document', documento: 'document' };
                 parts.push({ id: `${msg.id}-${index++}`, type: typeMap[tag], value: url });
              } else {
                 const trimmed = token.trim();
                 if (trimmed) {
                   let quote = undefined;
                   let actualText = trimmed;
                   if (safeLower(actualText).startsWith('citação:')) {
                     const newLineIndex = actualText.indexOf('\n');
                     if (newLineIndex !== -1) {
                       quote = actualText.substring(8, newLineIndex).trim();
                       actualText = actualText.substring(newLineIndex).trim();
                     } else {
                       quote = actualText.substring(8).trim();
                       actualText = '';
                     }
                   }
                   if (actualText || quote) {
                     parts.push({ id: `${msg.id}-${index++}`, type: 'text', value: actualText, quote });
                   }
                 }
              }
            }
          }

          if (parts.length === 0) {
            return dateSeparator ? [dateSeparator] : [];
          }

          return [
            dateSeparator,
            ...parts.map((part, partIndex) => {
              const isVisualContinuation = Boolean(msg.visual_parent_id) && (msg.visual_index || 0) > 0;
              const isGroupMessage = lead.remotejid?.endsWith('@g.us');
              const participantName = msg.metadata?.participant_name || 
                                     msg.metadata?.pushname || 
                                     msg.metadata?.sender_name;
              const participantPhone = msg.metadata?.participant_phone || null;
              const prevParticipantName = prevMsg?.metadata?.participant_name ||
                                         prevMsg?.metadata?.pushname ||
                                         prevMsg?.metadata?.sender_name;

              const isSameSender = partIndex > 0 || isVisualContinuation || (
                msgIndex > 0 && 
                prevMsg && 
                prevMsg.message_direction === msg.message_direction && 
                (isInbound && isGroupMessage ? prevParticipantName === participantName : prevSignature === extractedSenderSignature)
              );
              const isSystem = msg.message_sender_type === 'sistema';
              const senderName = isInbound 
                ? lead.nome || 'Lead' 
                : isIA 
                  ? 'SDR IA' 
                  : isSystem 
                    ? 'Sistema' 
                    : (extractedSenderSignature || 
                       (msg.sent_by_user_id 
                          ? (users.find(x => x.id === msg.sent_by_user_id)?.nome 
                             || users.find(x => x.id === msg.sent_by_user_id)?.email?.split('@')[0]) 
                          : null) ||
                       'Atendente');
              
              if (isSystem && !part.value) return null;

              const handleReplyClick = () => {
                 const repText = part.type === 'text' ? part.value : part.type;
                 setReplyingTo({ text: repText, sender: senderName, msgId: msg.id });
              };

              const nextMsg = msgIndex < grouped.length - 1 ? grouped[msgIndex + 1] : null;
              const isLastOfSenderGroup = !nextMsg || 
                nextMsg.message_direction !== msg.message_direction || 
                nextMsg.message_sender_type === 'sistema' ||
                (new Date(nextMsg.created_at).toLocaleDateString() !== msgDate.toLocaleDateString());
              const showTail = isLastOfSenderGroup && (partIndex === parts.length - 1);

              const mappedStatus: 'sending' | 'sent' | 'delivered' | 'read' | 'imported' | undefined =
                !isInbound
                  ? (msg.send_status === 'pending' || msg.send_status === 'sending' ? 'sending'
                     : msg.send_status === 'delivered' ? 'delivered'
                     : msg.send_status === 'read' ? 'read'
                     : msg.send_status === 'failed' ? undefined
                     : msg.send_status === 'imported_unverified' ? 'imported'
                     : 'sent')
                  : undefined;

              const parsedReactions = msg.reactions
                ? Object.entries(msg.reactions).map(([emoji, val]) => ({
                    emoji,
                    count: typeof val === 'number' ? val : (Array.isArray(val) ? val.length : 1)
                  }))
                : [];

              if (isSystem) {
                if (!part.value) return null;
                return (
                  <div key={`${msg.id}-${partIndex}`} className="flex w-full justify-center my-2">
                    <SystemMessageBubble timestamp={formatSafe(msg.created_at, 'HH:mm')}>
                      {part.value}
                    </SystemMessageBubble>
                  </div>
                );
              }

              // Strip signature from quoted messages
              const stripSignature = (txt: string) => (txt ? txt.replace(/^\*[^*]+\*\n+/, '') : '');
              const quotedMsg = msg.quoted_message_id 
                ? messages.find(m => m.id === msg.quoted_message_id) 
                : null;

              let quoteSender = '';
              let quoteText = '';

              if (quotedMsg) {
                if (quotedMsg.message_direction === 'inbound') {
                  quoteSender = lead.nome || quotedMsg.metadata?.participant_name || quotedMsg.metadata?.sender_name || 'Lead';
                } else if (quotedMsg.message_sender_type === 'ia') {
                  quoteSender = 'SDR IA';
                } else if (quotedMsg.sent_by_user_id) {
                  const u = users.find(x => x.id === quotedMsg.sent_by_user_id);
                  quoteSender = u?.nome || u?.email?.split('@')[0] || 'Atendente';
                } else {
                  quoteSender = 'Atendente';
                }

                const rawContent = (quotedMsg.content || '').trim();
                const stripped = stripSignature(rawContent).trim();
                const isPlaceholder = ['[imagem]', '[áudio]', '[audio]', '[vídeo]', '[video]', '[documento]', '[sticker]', '[mídia]', '[midia]'].includes(stripped.toLowerCase());
                if (stripped && !isPlaceholder) {
                  quoteText = stripped;
                } else if (quotedMsg.message_type === 'image') {
                  quoteText = '📷 Imagem';
                } else if (quotedMsg.message_type === 'video') {
                  quoteText = '🎥 Vídeo';
                } else if (quotedMsg.message_type === 'audio' || quotedMsg.message_type === 'ptt' || quotedMsg.message_type === 'voice') {
                  quoteText = '🎤 Áudio';
                } else if (quotedMsg.message_type === 'document') {
                  quoteText = '📄 Documento';
                } else if (quotedMsg.message_type === 'sticker') {
                  quoteText = '🏷️ Figurinha';
                } else {
                  quoteText = stripped || rawContent;
                }
              }

              if (!quoteText && (partIndex === 0 || part.quote)) {
                quoteText = msg.quoted_message_content || part.quote || msg.metadata?.quoted_preview || '';
                quoteSender = msg.metadata?.quoted_sender_name || (isInbound ? 'Atendente' : (lead.nome || 'Lead'));
              }

              return (
                <div 
                  key={`${msg.id}-${partIndex}`} 
                  data-message-id={msg.id}
                  className={cn(
                    "flex w-full group animate-in fade-in slide-in-from-bottom-2 duration-300 relative items-end", 
                    isInbound ? "justify-start gap-1.5" : "justify-end gap-1.5",
                    isSameSender ? "mt-0.5" : "mt-2"
                  )}
                >
                  {isInbound && (
                    <div className="w-8 shrink-0 flex flex-col items-center justify-end select-none">
                      {!isSameSender ? (
                        <div className="relative">
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shadow-xs border border-black/5",
                            getParticipantColor(participantName || lead.nome || 'Lead')
                          )}>
                            {getParticipantInitials(participantName || lead.nome || 'Lead')}
                          </div>
                          {part.type === 'audio' && (
                            <div className="absolute -bottom-1 -right-1 w-4.5 h-4.5 bg-[#202c33] dark:bg-[#202c33] rounded-full border border-gray-100 dark:border-gray-800 flex items-center justify-center text-[#53bdeb] shadow-sm">
                              <Mic size={10} className="fill-[#53bdeb]" />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-8 h-8" />
                      )}
                    </div>
                  )}

                  {!isInbound && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center pr-1 py-1 gap-1 order-first">
                      <button onClick={handleReplyClick} className="text-gray-400 hover:text-gray-600 active:scale-95 transition-all p-1.5 rounded-full hover:bg-black/5" title="Responder">
                        <Reply size={16} />
                      </button>
                      <button onClick={() => togglePin(msg.id)} className={cn("active:scale-95 transition-all p-1.5 rounded-full hover:bg-black/5", msg.is_pinned ? "text-emerald-500" : "text-gray-400 hover:text-gray-600")} title="Fixar">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={msg.is_pinned ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2"><path d="M16 9V4l1 0V2H7v2l1 0v5c0 2.18-1.79 4-4 4v2h7v9l1 1 1-1v-9h7v-2c-2.21 0-4-1.82-4-4z"/></svg>
                      </button>
                      <button onClick={() => handleDeleteMessage(msg.id)} className="text-gray-400 hover:text-red-500 active:scale-95 transition-all p-1.5 rounded-full hover:bg-black/5" title="Excluir mensagem">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}

                  {/* Media Group Grid */}
                  {part.type === 'media_group_grid' && part.messages && (
                    <div className={cn("!w-auto max-w-[85%] md:max-w-[70%]", msg.is_pinned && "ring-2 ring-emerald-500/20")}>
                      <ImageBubble
                        key={mediaContextKey}
                        variant={isInbound ? "incoming" : "outgoing"}
                        onImageClick={(_messageId, url) => { setLightboxImage(url); setFullscreenImageUrl(url); }}
                        items={part.messages.map((m: any) => ({
                          messageId: m.id,
                          url: m.media_url || ''
                        }))}
                        timestamp={formatSafe(msg.created_at, 'HH:mm')}
                        status={mappedStatus}
                        showTail={showTail}
                        className="!w-auto"
                        onImageError={handleMediaError}
                        onRecover={handleManualMediaRecovery}
                        reloadKeys={reloadKeys}
                      />
                    </div>
                  )}

                  {/* Single Media (Audio, Video, Image, Document) */}
                  {(part.type === 'audio' || part.type === 'image' || part.type === 'video' || part.type === 'document') && (
                    <div className={cn("!w-auto max-w-[85%] md:max-w-[70%]", msg.is_pinned && "ring-2 ring-emerald-500/20")}>
                      <MediaDemand
                        key={mediaContextKey}
                        type={part.type}
                        contextKey={mediaContextKey}
                        onImageError={handleMediaError}
                        onRecover={handleManualMediaRecovery}
                        url={part.value || msg.media_url || ''} 
                        messageId={msg.id}
                        downloadName={msg.metadata?.file_name || msg.file_name}
                        initialTranscription={(msg as any)?.metadata?.transcription || (msg as any)?.transcricao}
                        onImageClick={(url) => { setLightboxImage(url); setFullscreenImageUrl(url); }}
                        onVideoClick={(url) => setLightboxVideo(url)}
                        onPlaybackChange={(isPlaying) => setMediaPlaying(msg.id, isPlaying)}
                        onMediaResolved={(newUrl) => {
                          updateMessageInCache(msg.id, { media_url: newUrl });
                        }}
                        onTranscriptionSuccess={(text) => {
                          updateMessageInCache(msg.id, {
                            metadata: { ...(msg.metadata || {}), transcription: text }
                          } as any);
                        }}
                        variant={isInbound ? 'incoming' : 'outgoing'}
                        timestamp={formatSafe(msg.created_at, 'HH:mm')}
                        status={mappedStatus}
                        showTail={showTail}
                        caption={part.type === 'image' || part.type === 'video' ? (text !== '' && !MEDIA_PLACEHOLDERS.includes(text.toLowerCase()) ? text : undefined) : undefined}
                        onSameUrlReload={handleSameUrlReload}
                        reloadKey={reloadKeys[msg.id] || 0}
                      />
                    </div>
                  )}

                  {/* Text Message Bubble */}
                  {part.type === 'text' && (
                    <ChatBubble
                      variant={isInbound ? "incoming" : "outgoing"}
                      timestamp={formatSafe(msg.created_at, 'HH:mm')}
                      status={mappedStatus}
                      showTail={showTail}
                      isGroupChat={Boolean(isInbound && isGroupMessage && !isSameSender && partIndex === 0)}
                      sender={participantName || (participantPhone ? `+${participantPhone}` : undefined)}
                      senderColor={participantName ? getParticipantTextColor(participantName) : undefined}
                      reactions={parsedReactions}
                      className={cn(
                        "!w-auto max-w-[85%] md:max-w-[70%]",
                        msg.is_private && "[&>div]:!bg-amber-100 dark:[&>div]:!bg-amber-950/60 [&>div]:!border-amber-300 dark:[&>div]:!border-amber-800 [&>div]:border",
                        msg.is_pinned && "ring-2 ring-emerald-500/20"
                      )}
                    >
                      {msg.is_private && (
                        <div className="flex items-center gap-1 text-[10px] font-bold text-amber-800 dark:text-amber-400 mb-1 pb-0.5 border-b border-amber-200 dark:border-amber-800/50">
                          <Lock size={12} />
                          <span>NOTA INTERNA</span>
                        </div>
                      )}
                      {msg.is_pinned && (
                        <div className="flex items-center gap-1 text-[9px] font-bold text-emerald-600 dark:text-emerald-400 mb-0.5 uppercase tracking-tighter">
                           <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M16 9V4l1 0V2H7v2l1 0v5c0 2.18-1.79 4-4 4v2h7v9l1 1 1-1v-9h7v-2c-2.21 0-4-1.82-4-4z"/></svg>
                           Mensagem Fixada
                        </div>
                      )}
                      {isInbound && msg.context_produto_id && partIndex === 0 && (
                        <ProductContextCard produtoId={msg.context_produto_id} />
                      )}
                      {(() => {
                        const currentUserId = user?.id || profile?.id;
                        const senderIsOtherUser = !isIA && !isSystem && !msg.is_private
                          && msg.sent_by_user_id && currentUserId && msg.sent_by_user_id !== currentUserId;
                        const shouldShowSender = !isInbound && !msg.is_private && partIndex === 0 && (!isSameSender || senderIsOtherUser);
                        if (!shouldShowSender) return null;
                        return (
                          <div className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600 dark:text-blue-400 mb-1.5 pb-1 border-b border-blue-100/40 dark:border-blue-900/20">
                            {isIA ? <Bot size={12} className="opacity-75 shrink-0" /> : <User size={12} className="opacity-75 shrink-0" />}
                            <span className="truncate">
                              {isIA
                                ? "SDR IA"
                                : (extractedSenderSignature ||
                                   (msg.sent_by_user_id 
                                      ? (users.find(x => x.id === msg.sent_by_user_id)?.nome 
                                         || users.find(x => x.id === msg.sent_by_user_id)?.email?.split('@')[0]) 
                                      : null) ||
                                   "Atendente")}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Quoted Message */}
                      {quoteText && (
                        <ReplyPreview
                          author={quoteSender || 'Citação'}
                          authorColor={isInbound ? "var(--wa-emerald-600)" : "var(--wa-blue-500)"}
                          body={quoteText}
                          className="mb-1.5"
                        />
                      )}

                      {msg.is_deleted ? (
                        <div className="italic text-wa-text-secondary flex items-center gap-1.5 py-1">
                          <RotateCcw size={14} /> Esta mensagem foi apagada
                        </div>
                      ) : (
                        part.value && (
                          <ExpandableMessageText content={part.value} renderText={renderMessageTextWithMentions} />
                        )
                      )}
                    </ChatBubble>
                  )}

                  {isInbound && (
                     <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center pl-1 py-1 gap-1">
                       <EmojiPickerButton messageId={msg.id} onReact={handleReactToMessage} />
                       <button onClick={handleReplyClick} className="text-gray-400 hover:text-gray-600 active:scale-95 transition-all p-1.5 rounded-full hover:bg-black/5" title="Responder">
                         <Reply size={16} />
                       </button>
                       <button onClick={() => handleDeleteMessage(msg.id)} className="text-gray-400 hover:text-red-500 active:scale-95 transition-all p-1.5 rounded-full hover:bg-black/5" title="Excluir mensagem">
                         <Trash2 size={16} />
                       </button>
                     </div>
                  )}
                  {!isInbound && msg.send_status === 'failed' && !msg.external_message_id && !msg.error?.includes('Aceito pelo WhatsApp') && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center pr-2 py-2 gap-1">
                      <button 
                        onClick={async () => {
                          removeMessageFromCache(msg.id);
                          await handleSend(msg.content, msg.message_type, msg.media_url, msg.id);
                        }} 
                        className="text-rose-500 hover:text-rose-700 active:scale-95 transition-all px-2 py-1 text-xs font-semibold rounded bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/30 dark:hover:bg-rose-950/50 flex items-center gap-1 border border-rose-200 dark:border-rose-900"
                        title="Reenviar mensagem"
                      >
                        <RotateCcw size={13} />
                        Reenviar
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          ];
        });
      })()}

          {(isTyping || ((conversation as any)?.ai_typing && (conversation as any)?.ai_typing_at && new Date((conversation as any).ai_typing_at).getTime() > Date.now() - 30000)) && (
            <div className="flex w-full justify-start mt-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <TypingIndicator
                sender={((conversation as any)?.ai_typing && (conversation as any)?.ai_typing_at && new Date((conversation as any).ai_typing_at).getTime() > Date.now() - 30000) ? "Bia (IA)" : (lead.nome || "Lead")}
                senderColor={((conversation as any)?.ai_typing && (conversation as any)?.ai_typing_at && new Date((conversation as any).ai_typing_at).getTime() > Date.now() - 30000) ? "var(--wa-blue-500)" : "var(--wa-emerald-500)"}
              />
            </div>
          )}

        <div ref={messagesEndRef} />
          </MessageListErrorBoundary>
        )}
      </div>



       {/* Input Area */}
       <div className={cn(
         "bg-wa-composer py-2.5 flex flex-col gap-2 flex-shrink-0 z-20 border-t border-wa-border transition-colors shadow-[0_-1px_6px_rgba(0,0,0,0.02)] relative",
         isFloatingActionsExpanded ? "md:pl-20 px-3 md:pr-4" : "px-3 md:px-4"
       )}>
        {showTemplateMenu && (
          <div className={cn(
            "absolute bottom-20 bg-white/95 dark:bg-[#233138]/95 backdrop-blur-md border border-gray-200 dark:border-gray-700 shadow-xl rounded-2xl overflow-hidden z-30 animate-in slide-in-from-bottom-4 duration-200",
            "w-[calc(100vw-2rem)] max-w-sm left-4 right-4 md:w-80 md:right-auto",
            isFloatingActionsExpanded ? "md:left-24" : "md:left-4"
          )}>
            <div className="px-4 py-3 bg-gray-50/50 dark:bg-black/20 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Respostas Rápidas</span>
              <Zap size={14} className="text-amber-500 fill-amber-500" />
            </div>
            <ul className="max-h-64 overflow-y-auto py-1">
              {quickRepliesList.map((tpl, i) => (
                <li key={i}>
                  <button 
                    disabled={loading}
                    onClick={() => { setInput(tpl.text); setShowTemplateMenu(false); }}
                    className="w-full text-left px-4 py-3 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-[14px] text-gray-700 dark:text-gray-200 transition-colors border-b border-gray-50 dark:border-gray-800 last:border-none flex items-center justify-between group disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      <span className="truncate max-w-[200px]">{tpl.text}</span>
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded uppercase group-hover:bg-emerald-100 dark:group-hover:bg-emerald-800 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                      {tpl.trigger}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {showAttachMenu && (
          <div className={cn(
            "absolute bottom-20 bg-white/95 dark:bg-[#233138]/95 backdrop-blur-md border border-gray-200 dark:border-gray-700 shadow-xl rounded-2xl p-3 flex gap-4 z-30 animate-in zoom-in-95 duration-150 origin-bottom-left transition-colors",
            "w-[calc(100vw-2rem)] max-w-sm left-4 right-4 md:w-auto md:right-auto",
            isFloatingActionsExpanded ? "md:left-36" : "md:left-16"
          )}>
            <button onClick={() => { handleFileAction('image'); }} className="flex flex-col items-center gap-1.5 group">
              <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400 rounded-2xl flex items-center justify-center transition-all group-hover:bg-emerald-500 group-hover:text-white shadow-sm ring-1 ring-emerald-100 dark:ring-emerald-900">
                <Image className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Galeria</span>
            </button>
            <button onClick={() => { handleFileAction('video'); }} className="flex flex-col items-center gap-1.5 group">
               <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400 rounded-2xl flex items-center justify-center transition-all group-hover:bg-purple-500 group-hover:text-white shadow-sm ring-1 ring-purple-100 dark:ring-purple-900">
                 <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14v-4z"/><rect x="3" y="6" width="12" height="12" rx="2" ry="2"/></svg>
               </div>
               <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Vídeo</span>
            </button>
            <button onClick={() => { handleFileAction('document'); }} className="flex flex-col items-center gap-1.5 group">
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400 rounded-2xl flex items-center justify-center transition-all group-hover:bg-blue-500 group-hover:text-white shadow-sm ring-1 ring-blue-100 dark:ring-blue-900">
                <FileText className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Arquivo</span>
            </button>
            <button onClick={() => { startRecording(); setShowAttachMenu(false); }} className="flex flex-col items-center gap-1.5 group">
              <div className="w-12 h-12 bg-orange-50 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400 rounded-2xl flex items-center justify-center transition-all group-hover:bg-orange-500 group-hover:text-white shadow-sm ring-1 ring-orange-100 dark:ring-orange-900">
                <Mic className="w-6 h-6" />
              </div>
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Áudio</span>
            </button>
          </div>
        )}

        {/* Input file invisivel */}
        <input 
          type="file" 
          ref={fileInputRef} 
          style={{ display: 'none' }} 
          onChange={onFileChange} 
          multiple
        />

        {/* Attached File Preview Panel (WhatsApp Web Style) */}
        {attachedFiles.length > 0 && (
          <div className="mx-3 mt-1.5 p-3.5 bg-gray-50 dark:bg-[#111B21] rounded-2xl border border-gray-200 dark:border-gray-700/60 shadow-md flex flex-col gap-2.5 animate-in slide-in-from-bottom-2 duration-200 z-10">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                Anexos selecionados ({attachedFiles.length})
              </span>
              <button 
                type="button" 
                onClick={() => {
                  attachedFiles.forEach(f => {
                    if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
                  });
                  setAttachedFiles([]);
                  setActiveAttachedFileId(null);
                }}
                className="text-[10px] font-bold text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 uppercase tracking-wider"
              >
                Limpar Todos
              </button>
            </div>

            <div className="flex items-center gap-3 overflow-x-auto py-1 scrollbar-none no-scrollbar select-none">
              {attachedFiles.map((file) => {
                const isActive = file.id === activeAttachedFileId;
                return (
                  <div
                    key={file.id}
                    onClick={() => setActiveAttachedFileId(file.id)}
                    className={cn(
                      "w-20 h-20 relative rounded-xl border bg-white dark:bg-[#202C33] p-1 shadow-xs shrink-0 flex items-center justify-center overflow-visible transition-all cursor-pointer",
                      isActive ? "border-[#00a884] ring-2 ring-[#00a884]/20 scale-105" : "border-gray-200 dark:border-gray-800 hover:opacity-90"
                    )}
                  >
                    {/* Thumbnail Image or Fallback File Icon */}
                    {file.type === 'image' ? (
                      <img src={file.previewUrl} alt="Thumbnail" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="w-full h-full bg-emerald-150/10 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-300 rounded-lg flex flex-col items-center justify-center p-1">
                        {file.type === 'video' ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14v-4z"/><rect x="3" y="6" width="12" height="12" rx="2" ry="2"/></svg>
                        ) : (
                          <FileText size={18} />
                        )}
                        <span className="text-[7px] font-bold text-center truncate w-full mt-1 px-0.5">{file.name}</span>
                      </div>
                    )}

                    {/* Uploading Loader overlay */}
                    {file.isUploading && (
                      <div className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center text-white backdrop-blur-[1px]">
                        <RefreshCw size={14} className="animate-spin text-[#00a884]" />
                      </div>
                    )}

                    {/* Individual Remove Button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
                        const newFiles = attachedFiles.filter(f => f.id !== file.id);
                        setAttachedFiles(newFiles);
                        if (activeAttachedFileId === file.id) {
                          setActiveAttachedFileId(newFiles[0]?.id || null);
                        }
                      }}
                      className="absolute -top-1.5 -right-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-full p-0.5 shadow-md hover:scale-115 active:scale-90 transition-all cursor-pointer z-10 flex items-center justify-center"
                      title="Remover anexo"
                    >
                      <X size={11} strokeWidth={3} />
                    </button>
                  </div>
                );
              })}

              {/* "Add more" dashed card */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-[#00a884] hover:text-[#00a884] text-gray-400 dark:text-gray-500 flex flex-col items-center justify-center gap-1 shrink-0 bg-white dark:bg-[#202C33] transition-all cursor-pointer"
                title="Adicionar mais arquivos"
              >
                <Plus size={18} />
                <span className="text-[9px] font-bold uppercase tracking-wider">Adicionar</span>
              </button>
            </div>
          </div>
        )}

        {replyingTo && (
           <div className="mx-4 mt-2 relative animate-in slide-in-from-bottom-2 duration-300">
             <ReplyPreview
               author={replyingTo.sender}
               authorColor="var(--wa-emerald-600)"
               body={replyingTo.text}
               className="mb-0 bg-gray-50/80 dark:bg-[#111B21]/80 shadow-xs border border-wa-border/50 pr-8"
             />
             <button
               onClick={() => setReplyingTo(null)}
               className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-all cursor-pointer z-10"
               title="Cancelar resposta"
             >
               <X size={14} strokeWidth={2.5} />
             </button>
          </div>
        )}

        {/* Canned Responses Autocomplete */}
        {showAutocomplete && (
          <CannedResponsesAutocomplete 
            text={input} 
            onSelect={(replacement) => {
              setInput(replacement);
              setShowAutocomplete(false);
            }} 
            onClose={() => setShowAutocomplete(false)} 
          />
        )}

        {!isPrivateMode && !hasConnectedInstance && !connectionsLoading && (
          <div className="mx-4 mt-2 px-4 py-2.5 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300 rounded-xl border-l-[6px] border-red-500 flex items-center gap-2.5 shadow-sm ring-1 ring-black/5 dark:ring-white/5 text-xs font-medium animate-in slide-in-from-bottom-1 duration-200">
            <ShieldAlert size={16} className="text-red-600 dark:text-red-400 shrink-0" />
            <span className="flex-1">
              Nenhuma instância do WhatsApp conectada. Ative nas configurações ou ative o modo <b>Nota Interna</b> (ícone de cadeado) para salvar anotações de uso interno.
            </span>
          </div>
        )}

        {/* Emoji Quick Picker */}
        {showEmojiPicker && (
          <div className={cn(
            "absolute bottom-20 bg-white/95 dark:bg-[#233138]/95 backdrop-blur-md border border-gray-200 dark:border-gray-700 shadow-xl rounded-2xl p-2.5 z-30 animate-in zoom-in-95 duration-150 origin-bottom-left",
            "w-[calc(100vw-2rem)] max-w-xs left-3 md:left-4"
          )}>
            <div className="flex items-center justify-between px-2 pb-1.5 border-b border-gray-100 dark:border-gray-800 text-[11px] font-semibold text-gray-500 dark:text-gray-400">
              <span>Emojis</span>
              <button 
                type="button" 
                onClick={() => setShowEmojiPicker(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full"
              >
                <X size={13} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 pt-2">
              {['😀', '😂', '😍', '👍', '🙏', '👏', '🎉', '🔥', '❤️', '🤝', '😊', '👌', '⭐', '✨'].map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => {
                    setInput(prev => prev + emoji);
                    adjustTextareaHeight();
                    textareaRef.current?.focus();
                  }}
                  className="w-8 h-8 flex items-center justify-center text-lg hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-transform active:scale-90 cursor-pointer"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 z-20">
          {!recording ? (
            <>
              {/* Left Icons: Emoji, Attach File, Quick Reply (wa-ui standard) */}
              <div className="flex items-center gap-0.5 shrink-0">
                {/* "+" button on mobile for quick access to actions drawer */}
                <button
                  type="button"
                  onClick={() => setShowMobileActions(true)}
                  className="md:hidden p-2 text-emerald-600 dark:text-emerald-400 hover:bg-black/5 dark:hover:bg-white/5 rounded-full active:scale-90 transition-all shrink-0"
                  title="Menu de Ações"
                >
                  <Plus size={24} />
                </button>

                {/* 1. Emoji Button */}
                <button
                  type="button"
                  onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowAttachMenu(false); setShowTemplateMenu(false); }}
                  className={cn(
                    "p-2 transition-all rounded-full active:scale-90",
                    showEmojiPicker ? "bg-black/10 dark:bg-white/10 text-emerald-600 dark:text-emerald-400" : "text-[#54656f] dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5"
                  )}
                  title="Emojis"
                >
                  <Smile size={24} />
                </button>

                {/* 2. Attach File Button */}
                <div className="relative">
                  <button 
                    type="button"
                    onClick={() => { setShowAttachMenu(!showAttachMenu); setShowTemplateMenu(false); setShowEmojiPicker(false); }}
                    className={cn(
                      "p-2 transition-all rounded-full active:scale-90",
                      showAttachMenu ? "bg-black/10 dark:bg-white/10 text-emerald-600 dark:text-emerald-400" : "text-[#54656f] dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5"
                    )}
                    title="Anexar arquivo"
                  >
                    <Paperclip size={24} className={cn("rotate-45", showAttachMenu && "text-emerald-500")} />
                  </button>
                </div>

                {/* 3. Quick Reply Button */}
                <button 
                  type="button"
                  onClick={() => { setShowTemplateMenu(!showTemplateMenu); setShowAttachMenu(false); setShowEmojiPicker(false); }} 
                  className={cn(
                    "p-2 transition-all rounded-full active:scale-90",
                    showTemplateMenu ? "bg-black/10 dark:bg-white/10 text-amber-500" : "text-[#54656f] dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5"
                  )}
                  title="Respostas Rápidas"
                >
                  <Zap size={24} className={cn(showTemplateMenu && "fill-amber-500")} />
                </button>

                {/* Desktop Extra: Catálogo */}
                {catalogEnabled && (
                  <button
                    type="button"
                    onClick={() => setCatalogPickerOpen(true)}
                    className="hidden md:flex p-2 transition-all rounded-full hover:bg-black/5 dark:hover:bg-white/5 active:scale-90 text-[#54656f] dark:text-gray-400"
                    title="Enviar item do catálogo"
                  >
                    <ShoppingBag size={22} className="hover:text-blue-600 dark:hover:text-blue-400" />
                  </button>
                )}

                {/* Desktop Extra: Private Note Lock Toggle */}
                <button
                  type="button"
                  onClick={() => setIsPrivateMode(!isPrivateMode)}
                  className={cn(
                    "hidden md:flex p-2 transition-all rounded-full active:scale-90 items-center gap-1",
                    isPrivateMode 
                      ? "bg-amber-500 text-white font-bold shadow-md ring-2 ring-amber-300" 
                      : "text-[#54656f] dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5"
                  )}
                  title={isPrivateMode ? "Modo Nota Interna Ativo" : "Ativar Nota Interna"}
                >
                  <Lock size={20} />
                </button>
              </div>
              
              {/* Central Input Box (wa-ui style: border-none, bg-transparent inside container) */}
              <div className={cn(
                "order-first basis-full md:order-none md:basis-auto md:flex-1 flex flex-col justify-center px-4 py-1.5 transition-all shadow-xs min-w-0 relative",
                isPrivateMode 
                  ? "bg-[#FEF3C7] dark:bg-[#451A03] border-2 border-dashed border-[#F59E0B] rounded-2xl" 
                  : "bg-white dark:bg-[#2A3942] rounded-lg border-none shadow-xs",
                replyingTo && !isPrivateMode ? "rounded-b-lg rounded-t-none" : ""
              )}>
                {isPrivateMode && (
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider mb-0.5">
                    <Lock size={12} className="text-amber-600 dark:text-amber-400" />
                    <span>Nota Interna (visível somente pela equipe)</span>
                  </div>
                )}
                <div className="flex items-end justify-between gap-2 min-w-0">
                  <textarea 
                    ref={textareaRef}
                    rows={1}
                    value={input}
                    disabled={loading || isUploadingFile}
                    onChange={e => handleInputChange(e.target.value)}
                    onPaste={handlePaste}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (input.trim() || attachedFile) {
                          handleSend();
                          resetTextareaHeight();
                        }
                      }
                    }}
                    placeholder={loading ? "Enviando..." : isUploadingFile ? "Enviando arquivo..." : isPrivateMode ? "Escreva uma nota interna..." : "Mensagem (digite / para respostas rápidas)"}
                    className="w-full py-1 text-[15px] border-none bg-transparent outline-none ring-0 focus:outline-none focus:ring-0 placeholder:text-[#8696a0] dark:placeholder:text-[#8696a0] resize-none overflow-y-auto no-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden leading-relaxed text-gray-900 dark:text-gray-100 transition-[height] duration-75 disabled:opacity-50 min-w-0"
                    style={{ maxHeight: '160px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  />
                  {input.length > 0 && (
                    <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 shrink-0 pb-1">
                      {input.length}/2000
                    </span>
                  )}
                </div>
              </div>

              {/* Right: Mic / Calendar / Circular Send Button */}
              <div className="flex items-center w-auto gap-1 shrink-0">
                {!input.trim() && !attachedFile && !isPrivateMode && (
                  <button 
                    type="button"
                    disabled={loading || isUploadingFile}
                    onClick={startRecording}
                    className="p-2.5 text-[#54656f] dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-all active:scale-90 group disabled:opacity-50 shrink-0"
                    title="Gravar Áudio"
                  >
                    <Mic size={24} className="group-hover:text-emerald-600 dark:group-hover:text-emerald-400" />
                  </button>
                )}
                {(input.trim() || attachedFile) && !isPrivateMode && (
                  <button 
                    type="button"
                    disabled={loading || isUploadingFile}
                    onClick={() => {
                      if (!hasConnectedInstance) {
                        toast.error('Nenhuma instância do WhatsApp está conectada. Ative uma instância nas configurações para agendar mensagens.');
                        return;
                      }
                      setPendingScheduleMessage({ message: input, type: 'text' });
                      setShowScheduleModal(true);
                      setInput('');
                      resetTextareaHeight();
                    }}
                    className="p-2 text-[#54656f] dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-all active:scale-90 disabled:opacity-50 shrink-0"
                    title="Agendar Mensagem"
                  >
                    <Calendar size={20} />
                  </button>
                )}
                <button 
                  type="button"
                  disabled={loading || isUploadingFile || (!input.trim() && !attachedFile)}
                  onClick={() => {
                    handleSend();
                    resetTextareaHeight();
                  }}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90 shrink-0",
                    (loading || isUploadingFile)
                      ? "bg-gray-400 text-white"
                      : (input.trim() || attachedFile)
                      ? (isPrivateMode ? "bg-amber-600 hover:bg-amber-700 text-white shadow-sm" : "bg-[#00a884] hover:bg-[#008f6f] text-white shadow-sm")
                      : "bg-gray-200 dark:bg-[#2a3942] text-gray-400 dark:text-gray-500 cursor-not-allowed shadow-none"
                  )}
                  title={isPrivateMode ? "Salvar Nota Interna" : "Enviar Mensagem"}
                >
                  {(loading || isUploadingFile) ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isPrivateMode ? (
                    <Lock size={18} className="fill-white text-white" />
                  ) : (
                    <Send size={18} className={cn((input.trim() || attachedFile) ? "fill-white text-white ml-0.5" : "text-gray-400 dark:text-gray-500 ml-0.5")} />
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-between bg-white dark:bg-[#202C33] rounded-2xl px-6 py-2.5 border-2 border-emerald-500 shadow-lg animate-in slide-in-from-right-4 duration-300 transition-colors">
              <div className="flex items-center gap-4 text-emerald-600 dark:text-emerald-400 shrink-0">
                <div className="relative">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping absolute inset-0"></div>
                  <div className="w-3 h-3 bg-red-600 rounded-full relative"></div>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold tracking-tight tabular-nums">{formatDuration(recordingDuration)}</span>
                  <span className="text-[10px] text-emerald-500 dark:text-emerald-400 font-bold uppercase tracking-wider">Gravando Áudio</span>
                </div>
              </div>
              <div className="flex-1 max-w-xs md:max-w-md mx-4 h-8 flex items-center justify-center">
                <RecordingWaveform stream={recordingStream} isRecording={recording} />
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={cancelRecording}
                  className="text-red-500 hover:text-red-600 font-bold text-xs uppercase tracking-widest px-3 py-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  onClick={() => finishRecording('schedule')}
                  className="text-emerald-500 dark:text-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-300 font-bold text-xs uppercase tracking-widest px-3 py-1.5 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Calendar size={14} />
                  Agendar Mensagem
                </button>
                <button 
                  onClick={() => finishRecording('send')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-xl text-xs font-bold uppercase tracking-widest shadow-md transition-all active:scale-95"
                >
                  Enviar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Drawer Overlay & Container */}
      {activeDrawer && (
        <div className="absolute inset-0 sm:inset-y-0 sm:right-0 sm:left-auto z-[50] flex transition-all duration-300 animate-in slide-in-from-right-4">
          <div className="w-full sm:w-[380px] bg-white dark:bg-[#111B21] border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col transition-colors">
            <div className="h-14 px-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-white dark:bg-[#202C33] md:bg-gray-50/50 md:dark:bg-black/10 transition-colors">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setActiveDrawer(null)}
                  className="md:hidden p-1.5 -ml-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-full hover:bg-gray-150 dark:hover:bg-gray-800 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
                <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 uppercase tracking-widest flex items-center gap-2">
                  {activeDrawer === 'replies' && <><Zap size={16} /> Respostas Rápidas</>}
                  {activeDrawer === 'notes' && <><StickyNote size={16} /> Anotações</>}
                  {activeDrawer === 'reminders' && <><Bell size={16} /> Lembretes</>}
                  {activeDrawer === 'appointments' && <><CalendarClock size={16} /> Agendamentos</>}
                  {activeDrawer === 'actions' && <><Settings2 size={16} /> Ações</>}
                </h3>
              </div>
              <button 
                onClick={() => setActiveDrawer(null)}
                className="hidden md:block p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition-all"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto no-scrollbar">
              {activeDrawer === 'replies' && (
                <div className="p-2 space-y-1">
                   {quickRepliesList.map((r, i) => (
                     <button 
                       key={i} 
                       onClick={() => { setInput(r.text); setActiveDrawer(null); }}
                       className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-white/5 rounded-xl border border-transparent hover:border-gray-100 dark:hover:border-gray-800 transition-all group"
                     >
                       <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest">{r.trigger}</span>
                          <Zap size={10} className="text-gray-400 group-hover:text-amber-500 fill-amber-500/0 group-hover:fill-amber-500 transition-all" />
                       </div>
                       <p className="text-xs text-gray-700 dark:text-gray-200 line-clamp-2 leading-relaxed">{r.text}</p>
                     </button>
                   ))}
                </div>
              )}
              {activeDrawer === 'actions' && (
                <div className="p-4 space-y-4">
                   <div className="grid grid-cols-1 gap-3">
                      <button 
                         onClick={() => window.open(`https://wa.me/${lead.telefone.replace(/\D/g, '')}`, '_blank')}
                         className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-black/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-all"
                      >
                         <ExternalLink size={18} />
                         <span className="text-sm font-bold uppercase tracking-wide">Abrir no WhatsApp</span>
                      </button>
                   </div>
                </div>
              )}
              {activeDrawer === 'appointments' && (
                <ScheduledMessagesDrawer leadId={lead.id} remotejid={lead.remotejid} />
              )}
              {activeDrawer === 'notes' && (
                <NotesDrawer leadId={lead.id} />
              )}
              {activeDrawer === 'reminders' && (
                <RemindersDrawer leadId={lead.id} />
              )}
            </div>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#233138] rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 transition-colors">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center">
                  <Calendar size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Agendar Mensagem</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Escolha quando enviar o {pendingScheduleMessage?.type === 'audio' ? 'áudio' : 'texto'}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Data de Envio</label>
                  <input 
                    type="date" 
                    value={scheduledDate}
                    onChange={e => setScheduledDate(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-[#2A3942] border border-gray-100 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:text-gray-200 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Horário</label>
                  <input 
                    type="time" 
                    value={scheduledTime}
                    onChange={e => setScheduledTime(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-[#2A3942] border border-gray-100 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:text-gray-200 transition-all"
                  />
                </div>

                {pendingScheduleMessage?.type === 'audio' && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-xl flex items-center gap-3">
                    <div className="p-2 bg-emerald-500 text-white rounded-lg">
                      <Mic size={16} />
                    </div>
                    <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Áudio Gravado</span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-black/20 flex gap-3 transition-colors">
              <button 
                onClick={() => { setShowScheduleModal(false); setPendingScheduleMessage(null); }}
                className="flex-1 px-4 py-3 bg-white dark:bg-[#2A3942] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button 
                disabled={isScheduling}
                onClick={handleScheduleMessage}
                className="flex-[2] px-4 py-3 bg-emerald-500 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-emerald-600 transition-all active:scale-95 shadow-lg shadow-emerald-500/30 disabled:opacity-50"
              >
                {isScheduling ? 'Agendando...' : 'Confirmar Agendamento'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Mobile Bottom Sheet Actions Menu */}
      {showMobileActions && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[150] flex items-end justify-center md:hidden animate-in fade-in duration-200"
          onClick={() => setShowMobileActions(false)}
        >
          <div 
            className="w-full bg-white dark:bg-[#1F2C34] rounded-t-[2rem] px-6 pb-8 pt-4 shadow-2xl flex flex-col max-h-[90vh] animate-in slide-in-from-bottom-full duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grabber handle */}
            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-700 rounded-full mx-auto mb-5 shrink-0" />
            
            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 text-center mb-6">
              Ações e Recursos
            </h4>

            <div className="grid grid-cols-4 gap-y-6 gap-x-2">
              {[
                { 
                  id: 'profile', 
                  icon: UserRound, 
                  label: 'Perfil', 
                  color: 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20' 
                },
                { 
                  id: 'replies', 
                  icon: Zap, 
                  label: 'Respostas', 
                  color: 'bg-amber-500 text-white shadow-lg shadow-amber-500/20' 
                },
                ...(catalogEnabled ? [{
                  id: 'catalog',
                  icon: ShoppingBag,
                  label: 'Catálogo',
                  color: 'bg-blue-600 text-white shadow-lg shadow-blue-600/20',
                  onClick: () => {
                    setCatalogPickerOpen(true);
                  }
                }] : []),
                { 
                  id: 'attachments', 
                  icon: Paperclip, 
                  label: 'Anexos', 
                  color: 'bg-teal-500 text-white shadow-lg shadow-teal-500/20',
                  onClick: () => {
                    setShowAttachMenu(true);
                  }
                },
                { 
                  id: 'notes', 
                  icon: StickyNote, 
                  label: 'Notas', 
                  color: 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' 
                },
                { 
                  id: 'reminders', 
                  icon: Bell, 
                  label: 'Lembretes', 
                  color: 'bg-rose-500 text-white shadow-lg shadow-rose-500/20' 
                },
                { 
                  id: 'appointments', 
                  icon: CalendarClock, 
                  label: 'Agendar', 
                  color: 'bg-blue-500 text-white shadow-lg shadow-blue-500/20' 
                },
                { 
                  id: 'actions', 
                  icon: Settings2, 
                  label: 'Ações', 
                  color: 'bg-purple-500 text-white shadow-lg shadow-purple-500/20' 
                },
              ].map((item) => {
                const handleClick = async () => {
                  setShowMobileActions(false);
                  if (item.onClick) {
                    await item.onClick();
                  } else {
                    toggleDrawer(item.id as any);
                  }
                };

                return (
                  <button
                    key={item.id}
                    onClick={handleClick}
                    className="flex flex-col items-center gap-2 group outline-none"
                  >
                    <div className={cn(
                      "w-14 h-14 rounded-2xl flex items-center justify-center transition-transform active:scale-90 duration-150",
                      item.color
                    )}>
                      <item.icon size={26} className={cn(item.id === 'attachments' && "rotate-45")} />
                    </div>
                    <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-tight text-center leading-tight">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setShowMobileActions(false)}
              className="mt-8 w-full py-3.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold rounded-xl text-xs uppercase tracking-widest hover:bg-gray-200 active:scale-95 transition-all"
            >
              Fechar Menu
            </button>
          </div>
        </div>
      )}
      
      <LeadDrawer 
        lead={lead} 
        open={leadDrawerOpen} 
        onClose={() => setLeadDrawerOpen(false)} 
        onDeleted={onBack}
      />

      <CatalogPicker
        open={catalogPickerOpen}
        leadId={lead.id}
        leadLojaId={lead.loja_id}
        onClose={() => setCatalogPickerOpen(false)}
        onSent={async () => {
          try {
            await refetchMessages();
          } catch (e) {
            console.error("Failed to reload messages:", e);
          }
        }}
      />

      {/* WhatsApp-Style Fullscreen Attachment Preview Overlay */}
      {attachedFiles.length > 0 && attachedFile && (
        <div className="fixed inset-0 bg-[#0B141A] z-[200] flex flex-col justify-between select-none transition-all duration-300 animate-in fade-in zoom-in-95 duration-200">
          {/* Top Bar */}
          <div className="h-16 px-6 flex items-center justify-between border-b border-gray-800 bg-[#111B21]">
            <button 
              type="button"
              onClick={() => {
                attachedFiles.forEach(f => {
                  if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
                });
                setAttachedFiles([]);
                setActiveAttachedFileId(null);
              }}
              className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800 transition-colors"
              title="Cancelar"
            >
              <X size={24} />
            </button>

            {/* WhatsApp Tools */}
            <div className="flex items-center gap-4 text-gray-400">
              <button type="button" className="p-2 hover:text-white rounded-full hover:bg-gray-800 transition-colors disabled:opacity-30 cursor-not-allowed" disabled><Crop size={20} /></button>
              <button type="button" className="p-2 hover:text-white rounded-full hover:bg-gray-800 transition-colors disabled:opacity-30 cursor-not-allowed" disabled><Wand2 size={20} /></button>
              <button type="button" className="p-2 hover:text-white rounded-full hover:bg-gray-800 transition-colors disabled:opacity-30 cursor-not-allowed" disabled><Pencil size={20} /></button>
              <button type="button" className="p-2 hover:text-white rounded-full hover:bg-gray-800 transition-colors disabled:opacity-30 cursor-not-allowed" disabled><Type size={20} /></button>
              <button type="button" className="p-2 hover:text-white rounded-full hover:bg-gray-800 transition-colors disabled:opacity-30 cursor-not-allowed" disabled><Smile size={20} /></button>
            </div>

            <div className="flex items-center gap-2 text-gray-400">
              <button type="button" className="p-2 hover:text-white rounded-full hover:bg-gray-800 transition-colors disabled:opacity-30 cursor-not-allowed" disabled><RotateCw size={20} /></button>
              <button type="button" className="p-2 hover:text-white rounded-full hover:bg-gray-800 transition-colors disabled:opacity-30 cursor-not-allowed" disabled><Download size={20} /></button>
            </div>
          </div>

          {/* Center Preview */}
          <div className="flex-1 flex flex-col items-center justify-center p-4 min-h-0 bg-[#0B141A]">
            {attachedFile.type === 'image' ? (
              <img 
                src={attachedFile.previewUrl} 
                alt="Preview" 
                className="max-h-[60vh] max-w-full md:max-w-3xl object-contain rounded-xl shadow-2xl border border-gray-800" 
                referrerPolicy="no-referrer"
              />
            ) : attachedFile.type === 'video' ? (
              <video 
                src={attachedFile.previewUrl} 
                controls 
                className="max-h-[60vh] max-w-full md:max-w-3xl object-contain rounded-xl shadow-2xl border border-gray-800" 
              />
            ) : (
              <div className="flex flex-col items-center justify-center p-12 bg-[#111B21] rounded-3xl border border-gray-800 max-w-md w-full shadow-2xl">
                <FileText size={80} className="text-emerald-500 mb-4" />
                <p className="text-sm font-bold text-gray-200 text-center truncate w-full">{attachedFile.name}</p>
                <p className="text-xs text-gray-500 mt-2 font-mono uppercase tracking-wider">{attachedFile.sizeMb} • {attachedFile.type}</p>
              </div>
            )}

            {isUploadingFile && (
              <div className="mt-4 px-4 py-2 bg-[#111B21] border border-gray-800 rounded-full flex items-center gap-2 shadow-lg">
                <RefreshCw size={14} className="text-emerald-500 animate-spin" />
                <span className="text-xs font-semibold text-gray-300">Enviando arquivo para o servidor...</span>
              </div>
            )}
          </div>

          {/* Bottom Section */}
          <div className="bg-[#111B21] border-t border-gray-800 px-6 py-4 flex flex-col gap-4">
            {/* Caption Input Bar */}
            <div className="max-w-3xl w-full mx-auto relative flex items-center gap-3">
              <div className="flex-1 bg-[#202C33] rounded-xl px-4 py-3 flex items-center gap-3 border border-gray-700/50">
                <input 
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isUploadingFile && !loading) {
                      handleSend();
                    }
                  }}
                  placeholder="Adicione uma legenda..."
                  className="flex-1 bg-transparent text-sm text-gray-100 placeholder-gray-400 focus:outline-none"
                  autoFocus
                />
                <button type="button" className="text-gray-400 hover:text-white transition-colors">
                  <Smile size={20} />
                </button>
              </div>
            </div>

            {/* Thumbnail Bar & Send Button */}
            <div className="max-w-3xl w-full mx-auto flex items-center justify-between gap-4">
              {/* Thumbnails list */}
              <div className="flex items-center gap-3 overflow-x-auto py-1 max-w-[calc(100%-80px)] scrollbar-thin">
                {attachedFiles.map((fileObj) => {
                  const isActive = fileObj.id === activeAttachedFileId;
                  const isUploadingThis = fileObj.isUploading || !fileObj.uploadedUrl;

                  return (
                    <div key={fileObj.id} className="relative group shrink-0">
                      <button
                        type="button"
                        onClick={() => setActiveAttachedFileId(fileObj.id)}
                        className="cursor-pointer focus:outline-none block"
                      >
                        {fileObj.type === 'image' ? (
                          <img 
                            src={fileObj.previewUrl} 
                            alt={fileObj.name} 
                            className={cn(
                              "w-12 h-12 object-cover rounded-lg shadow-md border-2",
                              isActive ? "border-[#00a884]" : "border-transparent opacity-75 hover:opacity-100"
                            )} 
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className={cn(
                            "w-12 h-12 rounded-lg flex items-center justify-center shadow-md border-2",
                            isActive ? "border-[#00a884] bg-[#1F2C34] text-emerald-400" : "border-transparent bg-gray-800 text-gray-400 opacity-75 hover:opacity-100"
                          )}>
                            <FileText size={20} />
                          </div>
                        )}
                      </button>

                      {isUploadingThis && (
                        <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center pointer-events-none">
                          <RefreshCw size={14} className="text-white animate-spin" />
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (fileObj.previewUrl) URL.revokeObjectURL(fileObj.previewUrl);
                          
                          const remaining = attachedFiles.filter(f => f.id !== fileObj.id);
                          setAttachedFiles(remaining);

                          if (remaining.length === 0) {
                            setActiveAttachedFileId(null);
                          } else if (isActive) {
                            setActiveAttachedFileId(remaining[0].id);
                          }
                        }}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 shadow-md hover:scale-110 transition-transform cursor-pointer z-10"
                        title="Remover"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  );
                })}

                <button 
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-12 h-12 shrink-0 rounded-lg border-2 border-dashed border-gray-700 hover:border-gray-500 text-gray-500 hover:text-gray-300 flex items-center justify-center transition-all cursor-pointer bg-transparent"
                  title="Adicionar arquivo"
                >
                  <Plus size={20} />
                </button>
              </div>

              {/* Send Button */}
              <button
                type="button"
                disabled={loading || isUploadingFile}
                onClick={() => handleSend()}
                className={cn(
                  "w-14 h-14 shrink-0 rounded-full flex items-center justify-center transition-all active:scale-90 shadow-xl shadow-emerald-950/20",
                  (loading || isUploadingFile) ? "bg-gray-700 cursor-not-allowed" : "bg-[#00a884] hover:bg-[#008f6f] text-white"
                )}
                title="Enviar anexo"
              >
                {loading || isUploadingFile ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send size={24} className="fill-white text-white ml-0.5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Viewer Overlay with Zoom, Rotation and History */}
      {fullscreenImageUrl && (
        <div className="fixed inset-0 bg-black/95 z-[250] flex flex-col justify-between select-none animate-in fade-in duration-200">
          {/* Top Bar */}
          <div className="h-16 px-6 flex items-center justify-between bg-black/40 backdrop-blur-md border-b border-white/10 text-white z-10">
            <div className="flex items-center gap-3">
              <button 
                type="button"
                onClick={() => {
                  setFullscreenImageUrl(null);
                  setZoomLevel(1);
                  setRotation(0);
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                title="Fechar"
              >
                <X size={24} />
              </button>
              <span className="text-sm font-medium">Visualizador de Imagem</span>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <button 
                type="button"
                onClick={() => setZoomLevel(prev => Math.max(prev - 0.25, 0.5))}
                className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                title="Diminuir Zoom"
              >
                <ZoomOut size={20} />
              </button>
              <span className="text-xs font-mono w-12 text-center">{Math.round(zoomLevel * 100)}%</span>
              <button 
                type="button"
                onClick={() => setZoomLevel(prev => Math.min(prev + 0.25, 4))}
                className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                title="Aumentar Zoom"
              >
                <ZoomIn size={20} />
              </button>
              <button 
                type="button"
                onClick={() => setRotation(prev => (prev + 90) % 360)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors cursor-pointer"
                title="Rotacionar"
              >
                <RotateCw size={20} />
              </button>
              <button 
                type="button"
                onClick={() => { setZoomLevel(1); setRotation(0); }}
                className="px-3 py-1 bg-white/10 hover:bg-white/25 rounded-lg text-xs transition-colors cursor-pointer"
                title="Resetar"
              >
                Resetar
              </button>
            </div>
          </div>

          {/* Center Image Container */}
          <div className="flex-1 relative flex items-center justify-center overflow-hidden p-4">
            {/* Left navigation arrow */}
            {chatImagesHistory.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const currentIndex = chatImagesHistory.findIndex(img => img.url === fullscreenImageUrl);
                  if (currentIndex > 0) {
                    setFullscreenImageUrl(chatImagesHistory[currentIndex - 1].url);
                  } else {
                    // loop to last
                    setFullscreenImageUrl(chatImagesHistory[chatImagesHistory.length - 1].url);
                  }
                  setZoomLevel(1);
                  setRotation(0);
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/80 text-white rounded-full transition-all cursor-pointer z-10 hover:scale-105 active:scale-95"
                title="Imagem Anterior"
              >
                <ChevronLeft size={28} />
              </button>
            )}

            {/* The Image Itself */}
            <div 
              className="relative transition-transform duration-200 ease-out cursor-zoom-in" 
              style={{ transform: `scale(${zoomLevel}) rotate(${rotation}deg)` }}
              onWheel={(e) => {
                e.preventDefault();
                if (e.deltaY < 0) {
                  setZoomLevel(prev => Math.min(prev + 0.25, 4));
                } else {
                  setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
                }
              }}
            >
              <img 
                src={fullscreenImageUrl} 
                alt="Fullscreen Preview" 
                className="max-h-[70vh] max-w-[90vw] md:max-w-4xl object-contain rounded-lg shadow-2xl transition-all select-none pointer-events-none"
                referrerPolicy="no-referrer"
              />
            </div>

            {/* Right navigation arrow */}
            {chatImagesHistory.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const currentIndex = chatImagesHistory.findIndex(img => img.url === fullscreenImageUrl);
                  if (currentIndex !== -1 && currentIndex < chatImagesHistory.length - 1) {
                    setFullscreenImageUrl(chatImagesHistory[currentIndex + 1].url);
                  } else {
                    // loop to first
                    setFullscreenImageUrl(chatImagesHistory[0].url);
                  }
                  setZoomLevel(1);
                  setRotation(0);
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/80 text-white rounded-full transition-all cursor-pointer z-10 hover:scale-105 active:scale-95"
                title="Próxima Imagem"
              >
                <ChevronRight size={28} />
              </button>
            )}
          </div>

          {/* Bottom History/Gallery Panel */}
          {chatImagesHistory.length > 0 && (
            <div className="bg-black/60 border-t border-white/10 px-6 py-4 flex flex-col gap-2 backdrop-blur-md">
              <div className="flex items-center justify-between text-white text-xs font-semibold">
                <span>Histórico de Imagens</span>
                <span className="text-gray-400">
                  {chatImagesHistory.findIndex(img => img.url === fullscreenImageUrl) + 1} de {chatImagesHistory.length}
                </span>
              </div>

              <div className="flex items-center gap-3 overflow-x-auto py-2 scrollbar-none">
                {chatImagesHistory.map((img) => {
                  const isActive = img.url === fullscreenImageUrl;
                  return (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => {
                        setFullscreenImageUrl(img.url);
                        setZoomLevel(1);
                        setRotation(0);
                      }}
                      className={cn(
                        "relative rounded-lg overflow-hidden shrink-0 border-2 transition-all cursor-pointer",
                        isActive ? "border-[#00a884] scale-105" : "border-transparent opacity-60 hover:opacity-100"
                      )}
                    >
                      <img 
                        src={img.url} 
                        alt="Thumbnail" 
                        className="w-16 h-16 object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {lightboxImage && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightboxImage(null)}>
          <button className="absolute top-4 right-4 text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" onClick={(e) => { e.stopPropagation(); setLightboxImage(null); }}>
            <X className="w-6 h-6" />
          </button>
          <img src={lightboxImage} alt="Visualização ampliada" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {lightboxVideo && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-xs flex flex-col items-center justify-center p-4 cursor-pointer animate-in fade-in duration-150" onClick={() => setLightboxVideo(null)}>
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <a 
              href={lightboxVideo} 
              download 
              onClick={(e) => e.stopPropagation()} 
              className="text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
              title="Baixar vídeo"
            >
              <Download className="w-6 h-6" />
            </a>
            <button 
              className="text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer" 
              onClick={(e) => { e.stopPropagation(); setLightboxVideo(null); }}
              title="Fechar"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="max-w-4xl max-h-[85vh] w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <video 
              src={lightboxVideo} 
              controls 
              autoPlay 
              className="max-w-full max-h-[85vh] rounded-xl shadow-2xl bg-black" 
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduledMessagesDrawer({ leadId, remotejid }: { leadId: string, remotejid: string }) {
  const [messages, setMessages] = useState<ScheduledMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [type, setType] = useState<'text' | 'audio'>('text');
  const [message, setMessage] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState('14:00');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const audioInputRef = React.useRef<HTMLInputElement>(null);

  const fetchScheduled = async () => {
    setLoading(true);
    try {
      const data = await api.getScheduledMessages(leadId);
      setMessages(data);
    } catch (e: any) {
      console.error(e);
      toast.error('Erro ao buscar agendamentos: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduled();
  }, [leadId]);

  const handleCancel = async (msgId: string) => {
    if (confirm('Deseja cancelar este agendamento?')) {
      try {
        await api.cancelScheduledMessage(msgId);
        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, status: 'canceled' as const } : m));
        toast.success('Agendamento cancelado');
      } catch (e: any) {
        toast.error('Erro ao cancelar: ' + e.message);
      }
    }
  };

  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsSubmitting(true);
      const { url } = await api.uploadAudio(file);
      setMediaUrl(url);
      toast.success('Áudio carregado com sucesso');
    } catch (e: any) {
      toast.error('Erro no upload: ' + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (type === 'text' && !message) return toast.error('Digite a mensagem');
    if (type === 'audio' && !mediaUrl) return toast.error('Faça upload do áudio primeiro');

    try {
      setIsSubmitting(true);
      const scheduledAt = `${date}T${time}:00`;
      await api.scheduleMessage(leadId, remotejid, message, scheduledAt, type, mediaUrl);
      toast.success('Mensagem agendada');
      setShowForm(false);
      setMessage('');
      setMediaUrl('');
      fetchScheduled();
    } catch (e: any) {
      toast.error('Erro ao agendar: ' + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-[#111B21]">
        <button 
          onClick={() => setShowForm(!showForm)}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold uppercase tracking-widest transition-all active:scale-95 shadow-md"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? 'Fechar Formulário' : 'Novo Agendamento'}
        </button>
      </div>

      {showForm && (
        <div className="p-4 space-y-4 bg-gray-50 dark:bg-black/20 border-b border-gray-100 dark:border-gray-800 animate-in slide-in-from-top-2 duration-300">
          <div className="flex bg-white dark:bg-[#202C33] p-1 rounded-xl border border-gray-100 dark:border-gray-700">
            <button 
              onClick={() => setType('text')}
              className={cn("flex-1 py-1.5 rounded-lg text-[11px] font-bold uppercase transition-all", type === 'text' ? "bg-blue-600 text-white" : "text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5")}
            >
              Texto
            </button>
            <button 
              onClick={() => setType('audio')}
              className={cn("flex-1 py-1.5 rounded-lg text-[11px] font-bold uppercase transition-all", type === 'audio' ? "bg-blue-600 text-white" : "text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5")}
            >
              Áudio
            </button>
          </div>

          <div className="space-y-3">
            {type === 'text' ? (
              <div>
                <label className="text-[10px] text-gray-400 font-bold uppercase ml-1">Mensagem</label>
                <textarea 
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  className="w-full bg-white dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none h-20"
                />
              </div>
            ) : (
              <div>
                <label className="text-[10px] text-gray-400 font-bold uppercase ml-1">Upload de Áudio</label>
                <div className="mt-1 flex flex-col gap-2">
                  <input type="file" ref={audioInputRef} onChange={handleAudioUpload} accept="audio/*" className="hidden" />
                  <button 
                    onClick={() => audioInputRef.current?.click()}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
                  >
                    <Upload size={16} /> {mediaUrl ? 'Alterar Áudio' : 'Selecionar Arquivo'}
                  </button>
                  {mediaUrl && (
                    <div className="flex items-center gap-2 p-2 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 rounded-lg">
                      <div className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-md flex items-center justify-center">
                        <Check size={14} />
                      </div>
                      <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 flex-1 truncate">Áudio carregado</span>
                      <MediaDemand type="audio" url={mediaUrl!} />
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] text-gray-400 font-bold uppercase ml-1 mt-2">Observação (opcional)</label>
                    <input 
                      type="text"
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      className="w-full bg-white dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-800 dark:text-gray-200"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-gray-400 font-bold uppercase ml-1">Data</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full bg-white dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-800 dark:text-gray-200" />
              </div>
              <div>
                <label className="text-[10px] text-gray-400 font-bold uppercase ml-1">Hora</label>
                <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full bg-white dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 text-xs text-gray-800 dark:text-gray-200" />
              </div>
            </div>

            <button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold uppercase tracking-widest shadow-md flex items-center justify-center gap-2"
            >
              <Calendar size={14} /> {isSubmitting ? 'Agendando...' : 'Confirmar Agendamento'}
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Buscando...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400">
            <CalendarClock size={40} className="mb-4 opacity-20" />
            <p className="text-xs font-medium">Nenhum agendamento ativo</p>
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className="bg-white dark:bg-[#1A1D21] border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all relative overflow-hidden group">
              <div className={cn("absolute top-0 left-0 w-1 h-full", 
                msg.status === 'pending' ? "bg-amber-500" :
                msg.status === 'sent' ? "bg-emerald-500" : "bg-red-500"
              )} />
              
              <div className="flex items-center justify-between mb-3">
                <span className={cn(
                  "text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter",
                  msg.status === 'pending' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                  msg.status === 'sent' ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                  msg.status === 'canceled' ? "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400" :
                  "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                )}>
                  {msg.status === 'pending' ? 'Pendente' : msg.status === 'sent' ? 'Enviado' : msg.status === 'canceled' ? 'Cancelado' : 'Falhou'}
                </span>
                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 flex items-center gap-1">
                  <Clock size={10} />
                  {new Date(msg.scheduled_at).toLocaleString('pt-BR')}
                </span>
              </div>
              
              <div className="space-y-2">
                <p className="text-xs text-gray-700 dark:text-gray-200 leading-relaxed font-medium">
                  {msg.message || (msg.message_type === 'audio' ? 'Áudio agendado' : '')}
                </p>
                {msg.message_type === 'audio' && (
                  <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-black/20 rounded-xl border border-transparent dark:border-gray-800">
                    <MediaDemand type="audio" url={msg.media_url!} />
                  </div>
                )}
              </div>

              {msg.status === 'pending' && (
                <button 
                  onClick={() => handleCancel(msg.id)}
                  className="mt-3 w-full py-2 flex items-center justify-center gap-2 text-red-500 hover:text-red-600 dark:hover:text-red-400 text-[10px] font-bold uppercase tracking-widest transition-colors hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl"
                >
                  <Trash2 size={12} /> Cancelar Agendamento
                </button>
              )}
            </div>
          ))
        )}
      </div>
      <div className="p-4 bg-gray-50 dark:bg-black/20 border-t border-gray-100 dark:border-gray-800">
        <p className="text-[10px] text-gray-500 dark:text-gray-400 text-center italic">O envio será executado pelo n8n no horário agendado.</p>
      </div>
    </div>
  );
}

function NotesDrawer({ leadId }: { leadId: string }) {
  const users = useUsersStore(state => state.users);
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const data = await api.getNotes(leadId);
      setNotes(data);
    } catch (e: any) {
      console.error(e);
      toast.error('Erro ao buscar anotações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [leadId]);

  const handleSubmit = async () => {
    if (!noteText.trim()) return;
    
    setIsSubmitting(true);
    try {
      if (editingNoteId) {
        const updated = await api.updateNote(editingNoteId, noteText);
        setNotes(prev => prev.map(n => n.id === editingNoteId ? updated : n));
        setEditingNoteId(null);
        toast.success('Anotação atualizada');
      } else {
        const newNote = await api.addNote(leadId, noteText);
        setNotes(prev => [newNote, ...prev]);
        toast.success('Anotação salva');
      }
      setNoteText('');
    } catch (e: any) {
      toast.error('Erro ao salvar: ' + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (note: Note) => {
    setNoteText(note.note);
    setEditingNoteId(String(note.id));
  };

  const handleDelete = async (id: string) => {
    try {
      await api.deleteNote(id);
      setNotes(prev => prev.filter(n => n.id !== id));
      toast.success('Anotação excluída');
    } catch (e: any) {
      toast.error('Erro ao excluir: ' + e.message);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-black/10">
      <div className="p-4 bg-white dark:bg-[#111B21] border-b border-gray-100 dark:border-gray-800 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center">
            <StickyNote size={18} />
          </div>
          <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">
            {editingNoteId ? 'Editar Anotação' : 'Nova Anotação'}
          </h4>
        </div>
        <textarea 
          value={noteText}
          onChange={e => setNoteText(e.target.value)}
          placeholder="Escreva algo importante sobre o lead..."
          className="w-full bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 resize-none h-32 transition-all"
        />
        <div className="flex gap-2">
          {editingNoteId && (
            <button 
              onClick={() => {
                setEditingNoteId(null);
                setNoteText('');
              }}
              className="flex-1 py-2.5 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-gray-200 dark:hover:bg-white/10 transition-all"
            >
              Cancelar
            </button>
          )}
          <button 
            onClick={handleSubmit}
            disabled={isSubmitting || !noteText.trim()}
            className="flex-[2] py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs font-bold uppercase tracking-widest shadow-md disabled:opacity-50 transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            {isSubmitting ? 'Salvando...' : editingNoteId ? 'Atualizar' : 'Salvar Nota'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Buscando notas...</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400">
            <div className="w-16 h-16 bg-gray-100 dark:bg-black/10 rounded-full flex items-center justify-center mb-4">
              <StickyNote size={32} className="opacity-20" />
            </div>
            <p className="text-xs font-medium">Nenhuma anotação ainda</p>
            <p className="text-[10px] mt-1">As notas são visíveis apenas para o seu time.</p>
          </div>
        ) : (
          notes.map(note => (
            <div key={note.id} className="bg-white dark:bg-[#1A1D21] border border-gray-100 dark:border-gray-800 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all group">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-500">
                    {/* FIX (P8.5): buscar nome do usuário pelo ID */}
                    {(() => {
                      const noteUser = users.find(u => u.id === note.created_by);
                      const noteUserName = noteUser?.nome || noteUser?.email?.split('@')[0] || 'Operador';
                      return noteUserName.charAt(0).toUpperCase();
                    })()}
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-800 dark:text-gray-200">
                      {/* FIX (P8.5): buscar nome do usuário pelo ID */}
                      {(() => {
                        const noteUser = users.find(u => u.id === note.created_by);
                        return noteUser?.nome || noteUser?.email?.split('@')[0] || 'Operador';
                      })()}
                    </p>
                    <p className="text-[9px] text-gray-400">{formatSafe(note.created_at, 'dd/MM HH:mm')}</p>
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button 
                    onClick={() => handleEdit(note)}
                    className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                  >
                    <Settings2 size={12} />
                  </button>
                  <button 
                    onClick={() => handleDelete(String(note.id))}
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {note.note}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function RemindersDrawer({ leadId }: { leadId: string }) {
  const [reminders, setReminders] = useState<InternalReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [remindAt, setRemindAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const data = await api.getReminders(leadId);
      setReminders(data);
    } catch (e: any) {
      console.error(e);
      toast.error('Erro ao buscar lembretes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReminders();
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !remindAt) return;

    setIsSubmitting(true);
    try {
      const newReminder = await api.addReminder(leadId, {
        title,
        description,
        remind_at: new Date(remindAt).toISOString()
      });
      setReminders(prev => [newReminder, ...prev].sort((a, b) => new Date(a.remind_at).getTime() - new Date(b.remind_at).getTime()));
      setTitle('');
      setDescription('');
      setRemindAt('');
      toast.success('Lembrete criado');
    } catch (e: any) {
      toast.error('Erro ao criar: ' + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = async (id: number, status: 'done' | 'canceled') => {
    try {
      const updated = await api.updateReminder(id, { status });
      setReminders(prev => prev.map(r => r.id === id ? updated : r));
      toast.success(status === 'done' ? 'Lembrete concluído' : 'Lembrete cancelado');
    } catch (e: any) {
      toast.error('Erro ao atualizar: ' + e.message);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-black/10">
      <div className="p-4 bg-white dark:bg-[#111B21] border-b border-gray-100 dark:border-gray-800 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <Bell size={18} />
          </div>
          <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">Novo Lembrete</h4>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <input 
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="O que você precisa lembrar?"
            className="w-full bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            required
          />
          <textarea 
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Detalhes adicionais (opcional)"
            className="w-full bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none h-20 transition-all"
          />
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Data e Hora</label>
            <input 
              type="datetime-local"
              value={remindAt}
              onChange={e => setRemindAt(e.target.value)}
              className="w-full bg-gray-50 dark:bg-[#202C33] border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              required
            />
          </div>
          <button 
            type="submit"
            disabled={isSubmitting || !title.trim() || !remindAt}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold uppercase tracking-widest shadow-lg shadow-blue-500/20 disabled:opacity-50 transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            {isSubmitting ? 'Salvando...' : 'Criar Lembrete'}
          </button>
        </form>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Buscando lembretes...</p>
          </div>
        ) : reminders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400">
            <div className="w-16 h-16 bg-gray-100 dark:bg-black/10 rounded-full flex items-center justify-center mb-4">
              <Bell size={32} className="opacity-20" />
            </div>
            <p className="text-xs font-medium">Nenhum lembrete para este lead</p>
            <p className="text-[10px] mt-1">Lembretes ajudam você a não esquecer de retornar ou cobrar o cliente.</p>
          </div>
        ) : (
          reminders.map(reminder => (
            <div 
              key={reminder.id} 
              className={cn(
                "bg-white dark:bg-[#1A1D21] border rounded-2xl p-4 shadow-sm transition-all group",
                reminder.status === 'done' ? 'opacity-60 border-green-100 dark:border-green-900/20' : 
                reminder.status === 'canceled' ? 'opacity-60 border-red-100 dark:border-red-900/20 grayscale' : 
                'border-gray-100 dark:border-gray-800'
              )}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center",
                    reminder.status === 'done' ? 'bg-green-100 text-green-600' :
                    reminder.status === 'canceled' ? 'bg-red-100 text-red-600' :
                    'bg-blue-100 text-blue-600'
                  )}>
                    {reminder.status === 'done' ? <CheckCircle2 size={16} /> : <Clock size={16} />}
                  </div>
                  <div>
                    <h5 className={cn(
                      "text-sm font-bold",
                      reminder.status === 'done' ? 'line-through text-gray-500' : 'text-gray-800 dark:text-gray-200'
                    )}>
                      {reminder.title}
                    </h5>
                    <p className="text-[10px] font-bold text-gray-400">
                      {formatSafe(reminder.remind_at, 'dd/MM/yyyy HH:mm')}
                    </p>
                  </div>
                </div>
              </div>
              
              {reminder.description && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-wrap leading-relaxed">
                  {reminder.description}
                </p>
              )}

              {reminder.status === 'pending' && (
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleUpdateStatus(reminder.id, 'done')}
                    className="flex-1 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-[10px] font-bold uppercase"
                  >
                    Marcar Concluído
                  </button>
                  <button 
                    onClick={() => handleUpdateStatus(reminder.id, 'canceled')}
                    className="p-1.5 bg-gray-100 dark:bg-white/5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
              
              {(reminder.status === 'done' || reminder.status === 'canceled') && (
                <div className="flex items-center gap-1.5 mt-2">
                  <div className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    reminder.status === 'done' ? 'bg-green-500' : 'bg-red-500'
                  )} />
                  <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                    {reminder.status === 'done' ? `Concluído em ${formatSafe(reminder.completed_at || '', 'dd/MM HH:mm')}` : 'Cancelado'}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

